import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRfq } from 'actions/rfqActions';
import Loading from 'components/Loading';
import ApproverCommentsSection from 'views/Rfqs/ApproverCommentsSection';
import ReactHtmlParser from "react-html-parser";
import { getFormattedUnit } from "config";
import ComparisonExtraCharges from './ComparisonExtraCharges';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { ApproveRfq, rejectRfqApproval, requestRFQQuotationUpdate } from "actions/rfqActions";
import { useSnackbar } from "notistack";
import StaticPopupNew from 'components/StaticPopupNew';
import { MobileApprovalNegotiationSummary } from './ApprovalNegotiationSummary';
import AwardJustificationPanel from './AwardJustificationPanel';
import QuotationUpdateRequestHistory from './QuotationUpdateRequestHistory';

const MobileApprovalTabs = ({ activeTab, onChange, selectedRatesDisabled = false }) => (
    <div className="mt-3 grid grid-cols-2 rounded-xl bg-[#F2F2EA] p-1 text-xs font-semibold">
  <button
    type="button"
    onClick={() => {
      if (!selectedRatesDisabled) onChange("selectedRates");
    }}
    disabled={selectedRatesDisabled}
    className={`flex w-full items-center justify-center rounded-lg px-3 py-2 transition ${
      activeTab === "selectedRates"
        ? "bg-white text-[#004491] shadow-sm"
        : "text-gray-600"
    } ${selectedRatesDisabled ? "cursor-not-allowed opacity-60" : ""}`}
  >
    Selected Rates
  </button>

  <button
    type="button"
    onClick={() => onChange("negotiation")}
    className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 transition ${
      activeTab === "negotiation"
        ? "bg-white text-[#004491] shadow-sm"
        : "text-gray-600"
    }`}
  >
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-600"></span>
    </span>
    <span>Negotiation</span>

  </button>
</div>
);


const MobileSelectedRatesView = () => {
    const { rfqId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const rfqState = useSelector((state) => state.rfq);
    const rfqData = rfqState?.rfq; // This is the actual RFQ data
    const loading = rfqState?.loading;
    //   const loading = useSelector((state) => state.rfq?.loading);
    const userProfile = useSelector((state) => state.auth?.user);
    const { enqueueSnackbar } = useSnackbar();
    const entityId = userProfile?.userEntity?.entityId;
    const user = userProfile;
    const isEntityApprovalRequired = rfqData?.isApprovalRequired ?? true;
    const [selectedVendorIndex, setSelectedVendorIndex] = useState(0);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [totalSelectedQuotationValue, setTotalSelectedQuotationValue] = useState(0);
    const [selectedMaterialsMap, setSelectedMaterialsMap] = useState({});
    // const [chargeInputs, setChargeInputs] = useState({});
    const [approverComment, setApproverComment] = useState('');
    const [totalLowestExtraCharge, setTotalLowestExtraCharge] = useState(0);
    const [totalLowestPriceSum, setTotalLowestPriceSum] = useState(0);
    const [totalLowestLabourPriceSum, setTotalLowestLabourPriceSum] = useState(0);
    const [chargeInputs, setChargeInputs] = useState({});
    const [discountOnBasicAmountInputs, setDiscountOnBasicAmountInputs] = useState({});
    const [showRejectPopup, setShowRejectPopup] = useState(false);
    const [approvalRejectRemark, setApprovalRejectRemark] = useState('');
    const [rejectError, setRejectError] = useState('');
    const [showApprovePopup, setShowApprovePopup] = useState(false);
    const [showApprovalSummary, setShowApprovalSummary] = useState(false);
    const [activeMobileTab, setActiveMobileTab] = useState(() => {
        const queryTab = new URLSearchParams(location.search).get("tab");
        const stateTab = location.state?.initialMobileTab || location.state?.activeMobileTab;

        return stateTab === "negotiation" || queryTab === "negotiation"
            ? "negotiation"
            : "selectedRates";
    });
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [requestUpdateSubmitting, setRequestUpdateSubmitting] = useState(false);
    const [showRequestUpdatePopup, setShowRequestUpdatePopup] = useState(false);
    const [requestUpdateMessage, setRequestUpdateMessage] = useState("");

    const togglePopup = (skill) => {
        setSelectedDetail(skill);
        setIsPopupOpen(true);
    };

    // Filter suppliers with quotations
    const suppliersWithQuotation = rfqData?.suppliers?.filter(supplier =>
        supplier?.quotationReceived
    ) || [];
    // Sort suppliers by rank, keeping NA/unranked vendors at the end.
    const getRankOrder = (supplier) => {
        const rank = supplier?.vendorQuotations?.rank;
        if (!rank || rank === "NA") return Number.POSITIVE_INFINITY;

        const match = String(rank).match(/^L(\d+)$/i);
        return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
    };

    const sortedSuppliers = [...suppliersWithQuotation].sort((a, b) => {
        return getRankOrder(a) - getRankOrder(b);
    });
    const selectedVendor = sortedSuppliers[selectedVendorIndex];
    const canRequestSelectedVendorQuotationUpdate =
        String(rfqData?.status || '').toUpperCase() === 'PENDING APPROVAL' &&
        selectedVendor?.quotationReceived === true &&
        selectedVendor?.exclusionDetails?.isExcludedFromComparison !== true &&
        selectedVendor?.quotationUpdateRequest?.isRequested !== true;
    const isEditRatesLocked = rfqData?.status === 'APPROVED' || rfqData?.status === 'CLOSED';
    const isAdvancedTransport = rfqData?.transportOption === "perUnitAmount";
    const handleRequestQuotationUpdate = () => {
        if (!selectedVendor?.supplierId || !canRequestSelectedVendorQuotationUpdate || requestUpdateSubmitting) return;
        setRequestUpdateMessage("");
        setShowRequestUpdatePopup(true);
    };

    const handleCloseRequestUpdatePopup = () => {
        if (requestUpdateSubmitting) return;
        setShowRequestUpdatePopup(false);
        setRequestUpdateMessage("");
    };

    const handleConfirmRequestQuotationUpdate = async () => {
        if (!selectedVendor?.supplierId || !canRequestSelectedVendorQuotationUpdate || requestUpdateSubmitting) return;
        setRequestUpdateSubmitting(true);
        try {
            await dispatch(requestRFQQuotationUpdate({
                rfqId: rfqData?.rfqId,
                supplierId: selectedVendor.supplierId,
                entityId: rfqData?.entityId || entityId,
                managerMessage: requestUpdateMessage.trim(),
                actionBy: {
                    userId: userProfile?.userId,
                    name: userProfile?.name,
                    email: userProfile?.email,
                },
            }));
            setShowRequestUpdatePopup(false);
            setRequestUpdateMessage("");
            enqueueSnackbar("Quotation update request sent", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to request quotation update", { variant: "error" });
        } finally {
            setRequestUpdateSubmitting(false);
        }
    };
    const parseNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };
    const normalizeDecimalInput = (value) => {
        const nextValue = String(value);
        if (!/^\d*\.?\d{0,2}$/.test(nextValue)) return null;
        if (nextValue.length > 1 && /^0+[1-9]/.test(nextValue)) {
            return nextValue.replace(/^0+/, '');
        }
        return nextValue;
    };
    const roundAmount = (value) =>
        Math.round((parseNumber(value) + Number.EPSILON) * 100) / 100;
    const normalizeDiscountOnBasicAmountPercent = (value) => {
        const percent = parseNumber(value);
        if (percent <= 0) return 0;
        return Math.min(100, percent);
    };
    const getLatestQuotationSet = (supplier) =>
        supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};
    const getDiscountOnBasicAmountPercent = (supplierId, supplier) => {
        const editedValue = discountOnBasicAmountInputs?.[supplierId];
        if (editedValue !== undefined && editedValue !== null && editedValue !== "") {
            return normalizeDiscountOnBasicAmountPercent(editedValue);
        }

        return normalizeDiscountOnBasicAmountPercent(
            getLatestQuotationSet(supplier)?.discountOnBasicAmountPercent
        );
    };
    const getDiscountOnBasicAmount = (supplier, selectedMaterialAmount) =>
        roundAmount(
            selectedMaterialAmount *
            getDiscountOnBasicAmountPercent(supplier?.supplierId, supplier) /
            100
        );
    const handleDiscountOnBasicAmountChange = (supplierId, value) => {
        const sanitizedValue = String(value).replace(/[^\d.]/g, "");
        const normalizedValue = normalizeDecimalInput(sanitizedValue);
        if (normalizedValue === null) return;

        const numericValue = parseNumber(normalizedValue);
        const nextValue = numericValue > 100 ? "100" : normalizedValue;

        setDiscountOnBasicAmountInputs(prev => ({
            ...prev,
            [supplierId]: nextValue
        }));
    };
    const hasScopeMaterials = (scope) => Array.isArray(scope?.materials) && scope.materials.length > 0;
    const getScopeUnitRate = (scope) => {
        const quantity = parseNumber(scope?.quantity);
        const total = parseNumber(scope?.totalExclGst);
        return quantity > 0 ? total / quantity : total;
    };
    const findContractorVendorScope = (supplier, sectionId, scopeId) => {
        const section = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.contractorDetails?.find(
            item => String(item?.id) === String(sectionId)
        );
        return section?.scopes?.find(item => String(item?.id) === String(scopeId));
    };
    const buildContractorScopeMapForVendor = (supplier) => {
        if (!supplier || rfqData?.rfqType !== "contractor") return {};

        const selectedScopes = [];
        (rfqData?.providingFixingDetails || []).forEach((section) => {
            (section?.scopes || []).forEach((scope) => {
                const vendorScope = findContractorVendorScope(supplier, section?._id, scope?._id);
                if (!vendorScope) return;

                const isMaterialBacked = hasScopeMaterials(scope) || hasScopeMaterials(vendorScope);
                const quantity = parseNumber(scope?.quantity || vendorScope?.quantity || 1, 1);
                const selectedScopeRate = isMaterialBacked
                    ? parseNumber(vendorScope?.totalExclGst)
                    : getScopeUnitRate(vendorScope);

                selectedScopes.push({
                    sectionId: section?._id,
                    sectionName: section?.sectionName,
                    scopeId: scope?._id,
                    scopeOfWork: scope?.scopeOfWork,
                    quantity: scope?.quantity ?? vendorScope?.quantity,
                    selectedQuantity: quantity,
                    selectedScopeRate,
                    selectedSupplierPrice: isMaterialBacked ? parseNumber(vendorScope?.totalExclGst) : selectedScopeRate * quantity,
                    isMaterialBacked,
                });
            });
        });

        return selectedScopes.length ? { [supplier.supplierId]: selectedScopes } : {};
    };
    const getPlainMaterialName = (value, fallback = "material") => {
        const plainText = String(value || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

        return plainText || fallback;
    };
    const getRfqMaterialQuantity = (materialId) => {
        const material = rfqData?.materials?.find(item => String(item?._id) === String(materialId));
        return parseNumber(material?.quantity);
    };
    const getSelectedQuantity = (entry, materialId) =>
        parseNumber(
            entry?.selectedQuantity !== undefined && entry?.selectedQuantity !== null
                ? entry.selectedQuantity
                : getRfqMaterialQuantity(materialId)
        );
    const getSelectedQuantityForMaterial = (materialId, excludeVendorId = null) =>
        Object.entries(selectedMaterialsMap || {}).reduce((sum, [vendorId, materials]) => {
            if (excludeVendorId && String(vendorId) === String(excludeVendorId)) return sum;
            const selectedMaterial = materials?.find(item => String(item.materialId) === String(materialId));
            return sum + getSelectedQuantity(selectedMaterial, materialId);
        }, 0);
    const calculateSelectedTransportCost = (transportRate, selectedQuantity, existingTransportCost = 0) =>
        isAdvancedTransport
            ? parseNumber(transportRate) * parseNumber(selectedQuantity)
            : parseNumber(existingTransportCost);
    const calculateSelectedSupplierPrice = (itemRate, selectedQuantity, transportCost) =>
        (parseNumber(itemRate) * parseNumber(selectedQuantity)) + parseNumber(transportCost);
    const hasLoggedInUserApproved = rfqData?.approvalDetails?.some(
        (approval) =>
            approval?.status === "APPROVED" &&
            approval?.approvedBy?.userId === user?.userId
    );

    const calculateVendorExtraCharges = (vendorId) => {
        const vendor = sortedSuppliers.find(v => v.supplierId === vendorId);
        if (!vendor || !rfqData?.otherCharges) {
            return { charges: [], total: 0 };
        }

        const quotation =
            vendor.vendorQuotations?.quotationSets?.slice(-1)[0];

        const otherCharges = quotation?.otherCharges || {};

        const charges = Object.keys(rfqData.otherCharges)
            .filter(type => rfqData.otherCharges[type])
            .map(type => {
                let amount = Number(otherCharges[type]?.mrp || 0);

                if (
                    rfqData.isAdvancedTransport &&
                    type.toLowerCase() === 'transport'
                ) {
                    amount = (selectedMaterialsMap?.[vendorId] || []).reduce(
                        (sum, m) => sum + Number(m.selectedTransportCost || 0),
                        0
                    );
                }

                return {
                    name: type
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, s => s.toUpperCase()),
                    amount
                };
            })
            .filter(c => c.amount > 0);

        return {
            charges,
            total: charges.reduce((s, c) => s + c.amount, 0)
        };
    };


    // multiple selection per row / split order
    // const toggleMaterialSelection = (vendorId, rfqMaterial, vendorMaterial) => {
    //     setSelectedMaterialsMap(prev => {
    //         const updated = { ...prev };
    //         const existing = updated[vendorId] || [];

    //         const isSelected = existing.some(
    //             m => m.materialId === rfqMaterial._id
    //         );

    //         if (isSelected) {
    //             updated[vendorId] = existing.filter(
    //                 m => m.materialId !== rfqMaterial._id
    //             );
    //             if (updated[vendorId].length === 0) delete updated[vendorId];
    //         } else {
    //             updated[vendorId] = [
    //                 ...existing,
    //                 {
    //                     materialId: rfqMaterial._id,
    //                     quantity: rfqMaterial.quantity,
    //                     selectedDiscount: vendorMaterial.discount || 0,
    //                     selectedTransportRate: vendorMaterial.transportRate || 0,
    //                     selectedTransportCost: vendorMaterial.transportCost || 0,
    //                     selectedItemRate:
    //                         vendorMaterial.basePrice -
    //                         (vendorMaterial.basePrice * (vendorMaterial.discount || 0)) / 100,
    //                     selectedSupplierPrice:
    //                         (vendorMaterial.basePrice -
    //                             (vendorMaterial.basePrice * (vendorMaterial.discount || 0)) / 100) *
    //                         rfqMaterial.quantity +
    //                         (vendorMaterial.transportCost || 0),
    //                 },
    //             ];
    //         }

    //         return updated;
    //     });
    // };

    const toggleMaterialSelection = (vendorId, rfqMaterial, vendorMaterial) => {
        setSelectedMaterialsMap(prev => {
            const updated = { ...prev };

            const existing = updated[vendorId] || [];

            const isAlreadySelected = existing.some(
                m => String(m.materialId) === String(rfqMaterial._id)
            );

            // If already selected → UNCHECK
            if (isAlreadySelected) {
                updated[vendorId] = existing.filter(
                    m => String(m.materialId) !== String(rfqMaterial._id)
                );

                if (updated[vendorId].length === 0) {
                    delete updated[vendorId];
                }

                return updated;
            }

            const selectedQuantity = parseNumber(rfqMaterial.quantity) - getSelectedQuantityForMaterial(rfqMaterial._id);
            if (selectedQuantity <= 0) {
                enqueueSnackbar(`Full RFQ quantity is already allocated for ${getPlainMaterialName(rfqMaterial?.name, "this material")}.`, {
                    variant: "warning",
                    autoHideDuration: 4000,
                });
                return updated;
            }

            const selectedTransportRate = vendorMaterial?.transportRate || 0;
            const selectedTransportCost = calculateSelectedTransportCost(
                selectedTransportRate,
                selectedQuantity,
                vendorMaterial?.transportCost || 0
            );
            const selectedItemRate =
                vendorMaterial?.basePrice -
                (vendorMaterial?.basePrice *
                    (vendorMaterial?.discount || 0)) / 100;

            // Add to selected vendor
            updated[vendorId] = [
                ...(updated[vendorId] || []),
                {
                    materialId: rfqMaterial._id,
                    quantity: rfqMaterial.quantity,
                    selectedQuantity,
                    selectedDiscount: vendorMaterial?.discount || 0,
                    selectedTransportRate,
                    selectedTransportCost,
                    selectedItemRate,
                    selectedSupplierPrice: calculateSelectedSupplierPrice(
                        selectedItemRate,
                        selectedQuantity,
                        selectedTransportCost
                    ),
                },
            ];

            return updated;
        });
    };

    const handleDiscountChange = (
        vendorId,
        materialId,
        discount,
        vendorMaterial,
        quantity
    ) => {
        const normalizedDiscount = normalizeDecimalInput(discount);
        if (normalizedDiscount === null) return;
        const disc = normalizedDiscount === "" ? "" : Number(normalizedDiscount);

        setSelectedMaterialsMap(prev => {
            const updated = { ...prev };
            const existing = updated[vendorId] || [];

            let entry = existing.find(m => String(m.materialId) === String(materialId));

            // AUTO-CREATE ENTRY IF NOT SELECTED
            if (!entry) {
                entry = {
                    materialId,
                    quantity,
                    selectedQuantity: "",
                    selectedDiscount: disc,
                    selectedTransportRate: vendorMaterial.transportRate || 0,
                    selectedTransportCost: 0,
                    selectedItemRate: vendorMaterial.basePrice || 0,
                    selectedSupplierPrice: 0,
                };

                updated[vendorId] = [...existing, entry];
            }

            const previousDiscount = Number(entry.selectedDiscount || 0);
            entry.selectedDiscount = disc;
            const discountValue = Number(disc || 0);
            const discountBasePrice =
                previousDiscount === 0 && entry.selectedItemRate !== undefined
                    ? parseNumber(entry.selectedItemRate)
                    : parseNumber(vendorMaterial.basePrice);

            entry.selectedItemRate =
                discountBasePrice -
                (discountBasePrice * discountValue) / 100;

            entry.selectedSupplierPrice =
                calculateSelectedSupplierPrice(
                    entry.selectedItemRate,
                    getSelectedQuantity(entry, materialId),
                    entry.selectedTransportCost
                );

            return updated;
        });
    };

    const handleNetPriceChange = (vendorId, materialId, value, vendorMaterial) => {
        const normalizedValue = normalizeDecimalInput(value);
        if (normalizedValue === null) return;

        setSelectedMaterialsMap(prev => {
            const updated = { ...prev };
            const existing = updated[vendorId] || [];
            const entry = existing.find(m => String(m.materialId) === String(materialId));
            if (!entry) return updated;

            const selectedQuantity = getSelectedQuantity(entry, materialId);
            entry.selectedItemRate = normalizedValue;
            entry.selectedDiscount = 0;
            entry.selectedTransportRate = entry.selectedTransportRate ?? vendorMaterial?.transportRate ?? 0;
            entry.selectedTransportCost = calculateSelectedTransportCost(
                entry.selectedTransportRate,
                selectedQuantity,
                entry.selectedTransportCost ?? vendorMaterial?.transportCost ?? 0
            );
            entry.selectedSupplierPrice = calculateSelectedSupplierPrice(
                parseNumber(normalizedValue),
                selectedQuantity,
                entry.selectedTransportCost
            );

            return updated;
        });
    };

    const handleSelectedQuantityChange = (vendorId, materialId, quantity) => {
        const normalizedQuantity = normalizeDecimalInput(quantity);
        if (normalizedQuantity === null) return;

        setSelectedMaterialsMap(prev => {
            const updated = { ...prev };
            const entry = (updated[vendorId] || []).find(m => String(m.materialId) === String(materialId));
            if (!entry) return updated;

            const selectedQuantity = parseNumber(normalizedQuantity);
            entry.selectedQuantity = normalizedQuantity;
            entry.selectedTransportCost = calculateSelectedTransportCost(
                entry.selectedTransportRate,
                selectedQuantity,
                entry.selectedTransportCost
            );
            entry.selectedSupplierPrice = calculateSelectedSupplierPrice(
                entry.selectedItemRate,
                selectedQuantity,
                entry.selectedTransportCost
            );

            return updated;
        });
    };

    const lowestTotal =
        sortedSuppliers?.[0]?.vendorQuotations?.grandTotalWithoutGst || 0;

    const differenceFromL1 =
        totalSelectedQuotationValue - lowestTotal;

    const calculateTotalQuotationValueMobile = () => {
        return calculateClosingAmountsMobile().reduce(
            (sum, s) => sum + Number(s.closingAmount || 0),
            0
        );
    };

    const calculateClosingAmountsMobile = () => {
        if (!rfqData || !sortedSuppliers?.length) return [];

        return Object.keys(selectedMaterialsMap || {}).map((supplierId) => {
            const supplier = sortedSuppliers.find(
                item => String(item?.supplierId) === String(supplierId)
            );
            // Material total
            const materialTotal =
                (selectedMaterialsMap[supplierId] || []).reduce(
                    (sum, item) => sum + Number(item.selectedSupplierPrice || 0),
                    0
                );
            const discountOnBasicAmount = rfqData?.rfqType === "material"
                ? getDiscountOnBasicAmount(supplier, materialTotal)
                : 0;
            const materialTotalAfterDiscount = Math.max(0, materialTotal - discountOnBasicAmount);

            // Extra charges total
            const extraTotal =
                calculateVendorExtraCharges(supplierId)?.total || 0;

            return {
                supplierId,
                closingAmount: materialTotalAfterDiscount + extraTotal
            };
        });
    };

    const getSelectedLabourQuantity = (labour) => {
        const quantity = labour?.selectedQuantity !== undefined && labour?.selectedQuantity !== null
            ? labour.selectedQuantity
            : rfqData?.labourDetails?.find(item => String(item?._id) === String(labour?.labourId))?.quantity;
        return parseNumber(quantity);
    };

    const validateSplitQuantities = () => {
        const isSameQuantity = (selectedQuantity, rfqQuantity) =>
            Math.abs(Number(selectedQuantity || 0) - Number(rfqQuantity || 0)) < 0.0001;

        if (rfqData?.rfqType === "labour") {
            const selectedLabours = Object.values(selectedMaterialsMap || {}).flat();
            const quantityByLabour = selectedLabours.reduce((acc, labour) => {
                if (!labour?.labourId) return acc;
                acc[labour.labourId] = (acc[labour.labourId] || 0) + getSelectedLabourQuantity(labour);
                return acc;
            }, {});

            for (const labour of selectedLabours) {
                if (getSelectedLabourQuantity(labour) <= 0) {
                    const rfqLabour = rfqData?.labourDetails?.find(item => String(item?._id) === String(labour.labourId));
                    return `Quantity is mandatory for selected labour ${getPlainMaterialName(rfqLabour?.skill, "selected labour")}.`;
                }
            }

            for (const labour of rfqData?.labourDetails || []) {
                const selectedQuantity = quantityByLabour[labour._id] || 0;
                const rfqQuantity = parseNumber(labour.quantity);
                if (selectedQuantity > rfqQuantity) {
                    return `Selected quantity for ${getPlainMaterialName(labour?.skill, "labour")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please reduce the split quantity.`;
                }

                if (!isSameQuantity(selectedQuantity, rfqQuantity)) {
                    return `Selected quantity for ${getPlainMaterialName(labour?.skill, "labour")} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please allocate full quantity.`;
                }
            }

            return "";
        }

        if (rfqData?.rfqType !== "material") return "";

        const selectedMaterials = Object.values(selectedMaterialsMap || {}).flat();
        for (const material of selectedMaterials) {
            if (getSelectedQuantity(material, material.materialId) <= 0) {
                const rfqMaterial = rfqData?.materials?.find(item => String(item?._id) === String(material.materialId));
                return `Quantity is mandatory for selected material ${getPlainMaterialName(rfqMaterial?.name, "selected material")}.`;
            }
        }

        for (const material of rfqData?.materials || []) {
            const selectedQuantity = getSelectedQuantityForMaterial(material._id);
            const rfqQuantity = parseNumber(material.quantity);
            if (selectedQuantity > rfqQuantity) {
                return `Selected quantity for ${getPlainMaterialName(material?.name)} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please reduce the split quantity.`;
            }

            if (!isSameQuantity(selectedQuantity, rfqQuantity)) {
                return `Selected quantity for ${getPlainMaterialName(material?.name)} is ${selectedQuantity}, but RFQ quantity is ${rfqQuantity}. Please allocate full quantity.`;
            }
        }

        return "";
    };

    const filterChargeInputsBySuppliers = (chargeInputs, supplierIds) => {
        const allowedIds = new Set(
            supplierIds.map(s => s.supplierId)
        );

        return Object.keys(chargeInputs || {}).reduce((acc, vendorId) => {
            if (allowedIds.has(vendorId)) {
                acc[vendorId] = chargeInputs[vendorId];
            }
            return acc;
        }, {});
    };


    const handleApproveRfq = async () => {
        try {
            let supplierIds = [];
            let totalQuotationValue = 0;

            // CONTRACTOR → EXACT DESKTOP LOGIC
            if (rfqData?.rfqType === "contractor") {
                supplierIds = calculateClosingAmountsMobile();
                totalQuotationValue = calculateTotalQuotationValueMobile();
            }
            // MATERIAL & LABOUR → KEEP MOBILE LOGIC AS-IS
            else {
                const splitQuantityError = validateSplitQuantities();
                if (splitQuantityError) {
                    enqueueSnackbar(splitQuantityError, {
                        variant: "warning",
                        autoHideDuration: 6000
                    });
                    return;
                }
                supplierIds = calculateClosingAmountsMobile();
                totalQuotationValue = calculateTotalQuotationValueMobile();
            }

            const filteredChargeInputs =
                filterChargeInputsBySuppliers(chargeInputs, supplierIds);

            const payload = {
                rfqStatus: rfqData?.status,
                rfqId,
                entityId,
                isApprovalRequired: isEntityApprovalRequired,
                supplierIds,
                selectedMaterialsMap,
                chargeInputs: filteredChargeInputs, // FINAL SAFETY
                discountOnBasicAmountInputs,
                totalQuotationValue,
                approver: {
                    userId: user?.userId,
                    name: user?.name
                },
                approverComment: approverComment
            };



            await dispatch(ApproveRfq({ payload }));

            setShowApprovePopup(false);
            setShowApprovalSummary(true);

            enqueueSnackbar(
                "RFQ approved & closed successfully",
                { variant: "success", autoHideDuration: 3000 }
            );

        } catch (error) {
            enqueueSnackbar("Failed to approve RFQ", { variant: "error" });
        }
    };


    const handleRejectApproval = async () => {
        if (!approvalRejectRemark?.trim()) {
            setRejectError('Remark is required');
            return;
        }

        const payload = {
            rfqId,
            entityId,
            approvalRejectRemark,
            userId: user?.userId
        };

        // console.log("REJECT PAYLOAD (MOBILE)", payload);

        try {
            await dispatch(rejectRfqApproval({ payload }));

            enqueueSnackbar(
                'Approval rejected & RFQ sent for rework',
                { variant: 'success', autoHideDuration: 3000 }
            );

            setShowRejectPopup(false);
            setApprovalRejectRemark('');
            setRejectError('');

            navigate("/rfqs/workspace/pending-approvals");
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.message || 'An error occurred. Please try again.',
                { variant: 'error', autoHideDuration: 3000 }
            );
        }
    };

    const getLabourTotalBasicAmount = (supplier) => {
        const latestSet =
            supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (!latestSet) return 0;
        return Number(latestSet.totalWithoutGst || 0);
    };

    const getLabourTotalBasicAmountLive = (supplier) => {
        if (!supplier) return 0;

        const vendorId = supplier.supplierId;

        const latestSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const labourItems = latestSet?.labourItems || [];

        return labourItems.reduce((sum, labour) => {
            const edited =
                selectedMaterialsMap?.[vendorId]
                    ?.find(l => l.labourId === labour.id);

            const rate =
                edited?.selectedLabourRate ?? labour.labourRate;

            return sum + (Number(rate) * Number(labour.quantity || 0));
        }, 0);
    };

    const getContractorTotalBasicAmount = (supplier) => {
        if (!supplier) return 0;

        const latestSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const sections = latestSet?.contractorDetails || [];

        return sections.reduce(
            (sum, sec) => sum + Number(sec.sectionTotalExclGst || 0),
            0
        );
    };

    const getLowestContractorSectionRate = (sectionId) => {
        if (!sortedSuppliers?.length) return 0;

        const rates = sortedSuppliers
            .map(supplier => {
                const sec =
                    supplier.vendorQuotations
                        ?.quotationSets
                        ?.slice(-1)[0]
                        ?.contractorDetails
                        ?.find(s => s.id === sectionId);

                return Number(sec?.sectionTotalExclGst);
            })
            .filter(v => Number.isFinite(v));

        return rates.length ? Math.min(...rates) : 0;
    };



    useEffect(() => {
        if (
            !rfqData ||
            !selectedVendor ||
            rfqData.rfqType === 'contractor' // SKIP contractor
        ) return;

        const quotation =
            selectedVendor.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (!quotation?.otherCharges) return;

        setChargeInputs(prev => ({
            ...prev,
            [selectedVendor.supplierId]: Object.keys(rfqData.otherCharges || {})
                .reduce((acc, key) => {
                    acc[key] = quotation.otherCharges?.[key]?.mrp || 0;
                    return acc;
                }, {})
        }));
    }, [selectedVendor, rfqData]);


    useEffect(() => {
        if (rfqId && userProfile?.userEntity?.entityId) {
            // console.log('Dispatching fetchRfq with:', { entityId: userProfile.userEntity.entityId, rfqId });
            dispatch(fetchRfq({ entityId: userProfile.userEntity.entityId, rfqId }));
        }
    }, [rfqId, dispatch, userProfile]);

    useEffect(() => {
        if (!rfqData?.suppliers?.length) return;

        const restoredMap = {};

        rfqData.suppliers.forEach(supplier => {
            const vendorId = supplier.supplierId;

            const latestSet =
                supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

            if (!latestSet?.materialDetails?.length) return;

            const selectedMaterials = latestSet.materialDetails
                .filter(mat => mat.isSelected)
                .map(mat => ({
                    materialId: mat.id || mat.materialId,
                    quantity: mat.quantity,
                    selectedQuantity: mat.selectedQuantity ?? mat.quantity,
                    selectedDiscount: Number(mat.selectedDiscount ?? mat.discount ?? 0),
                    selectedTransportRate: Number(mat.selectedTransportRate ?? 0),
                    selectedTransportCost: Number(mat.selectedTransportCost ?? 0),
                    selectedItemRate: Number(mat.selectedItemRate ?? 0),
                    selectedSupplierPrice: Number(mat.selectedSupplierPrice ?? 0),
                }));

            if (selectedMaterials.length > 0) {
                restoredMap[vendorId] = selectedMaterials;
            }
        });

        // console.log('RESTORED selectedMaterialsMap FROM quotationSets:', restoredMap);

        setSelectedMaterialsMap(restoredMap);
    }, [rfqData]);

    useEffect(() => {
        if (rfqData?.rfqType !== "material" || !Array.isArray(rfqData?.suppliers)) return;

        setDiscountOnBasicAmountInputs((current) => {
            const next = { ...current };
            let hasChanges = false;

            rfqData.suppliers.forEach((supplier) => {
                const supplierId = supplier?.supplierId;
                if (!supplierId || next[supplierId] !== undefined) return;

                const percent = getLatestQuotationSet(supplier)?.discountOnBasicAmountPercent;
                if (percent !== undefined && percent !== null && percent !== "") {
                    next[supplierId] = String(percent);
                    hasChanges = true;
                }
            });

            return hasChanges ? next : current;
        });
    }, [rfqData]);

    useEffect(() => {
        if (rfqData?.rfqType !== 'contractor') return;

        const awardedSuppliers =
            rfqData?.suppliers?.filter(s => s.isAwarded) || [];

        if (awardedSuppliers.length === 0) {
            setSelectedVendors([]);
            setSelectedMaterialsMap({});
            setTotalSelectedQuotationValue(0);
            return;
        }

        const vendorIds = awardedSuppliers.map(s => s.supplierId);

        setSelectedVendors(vendorIds);

        setSelectedMaterialsMap(
            awardedSuppliers.reduce((acc, supplier) => ({
                ...acc,
                ...buildContractorScopeMapForVendor(supplier)
            }), {})
        );

        setTotalSelectedQuotationValue(
            awardedSuppliers.reduce(
                (sum, s) =>
                    sum +
                    Number(
                        s.vendorQuotations?.grandTotalWithoutGst || 0
                    ),
                0
            )
        );
    }, [rfqData]);

    useEffect(() => {
        if (rfqData?.rfqType !== 'labour') return;

        const restoredMap = {};

        rfqData?.suppliers?.forEach((supplier) => {
            const vendorId = supplier.supplierId;

            const latestSet =
                supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

            if (!latestSet?.labourItems?.length) return;

            const selectedLabours = latestSet.labourItems
                .filter(l => l.isSelected) // backend flag
                .map(labour => ({
                    labourId: labour.id,
                    quantity: labour.quantity,
                    selectedQuantity: labour.selectedQuantity ?? labour.quantity,
                    selectedLabourRate: labour.selectedLabourRate ?? labour.labourRate,
                    selectedSupplierPrice:
                        labour.selectedSupplierPrice ??
                        ((labour.selectedLabourRate ?? labour.labourRate) *
                            (labour.selectedQuantity ?? labour.quantity)),
                }));

            if (selectedLabours.length > 0) {
                restoredMap[vendorId] = selectedLabours;
            }
        });

        setSelectedMaterialsMap(restoredMap);
    }, [rfqData]);



    useEffect(() => {
        if (!rfqData?.supplierIds || !rfqData?.supplierIds.length) return;

        const restoredMap = {};

        rfqData.supplierIds.forEach(supplier => {
            if (!supplier?.supplierId) return;

            // desktop stores selected materials here
            const materials = supplier.materials || [];

            if (!materials.length) return;

            restoredMap[supplier.supplierId] = materials.map(mat => ({
                materialId: mat.materialId, // MUST MATCH rfqMaterial._id
                quantity: mat.quantity,
                selectedQuantity: mat.selectedQuantity ?? mat.quantity,
                selectedDiscount: mat.selectedDiscount || 0,
                selectedTransportRate: mat.selectedTransportRate || 0,
                selectedTransportCost: mat.selectedTransportCost || 0,
                selectedItemRate: mat.selectedItemRate || 0,
                selectedSupplierPrice: mat.selectedSupplierPrice || 0,
            }));
        });

        // console.log('Restored selectedMaterialsMap:', restoredMap);

        setSelectedMaterialsMap(restoredMap);
    }, [rfqData]);


    const calculateAllVendorsSelectedTotal = () => {
        if (!sortedSuppliers?.length) return 0;

        return sortedSuppliers.reduce((grandTotal, supplier) => {
            const vendorId = supplier.supplierId;

            // Material total for this vendor
            const materialTotal =
                (selectedMaterialsMap?.[vendorId] || []).reduce(
                    (sum, item) => sum + Number(item.selectedSupplierPrice || 0),
                    0
                );
            const discountOnBasicAmount = rfqData?.rfqType === "material"
                ? getDiscountOnBasicAmount(supplier, materialTotal)
                : 0;
            const materialTotalAfterDiscount = Math.max(0, materialTotal - discountOnBasicAmount);

            // Extra charges total for this vendor
            const extraTotal =
                calculateVendorExtraCharges(vendorId)?.total || 0;

            return grandTotal + materialTotalAfterDiscount + extraTotal;
        }, 0);
    };

    useEffect(() => {
        if (!sortedSuppliers?.length) {
            setTotalSelectedQuotationValue(0);
            return;
        }

        let totalSum = sortedSuppliers.reduce((sum, supplier) => {
            const selectedMaterials =
                selectedMaterialsMap?.[supplier.supplierId] || [];

            // Base from selected items
            const selectedMaterialBasePriceSum = selectedMaterials.reduce(
                (total, item) =>
                    total + (parseFloat(item.selectedSupplierPrice) || 0),
                0
            );
            const discountOnBasicAmount = rfqData?.rfqType === "material"
                ? getDiscountOnBasicAmount(supplier, selectedMaterialBasePriceSum)
                : 0;
            const selectedMaterialAmountAfterDiscount = Math.max(
                0,
                selectedMaterialBasePriceSum - discountOnBasicAmount
            );

            // Extra charges
            const supplierCharges = chargeInputs?.[supplier.supplierId] || {};

            const totalExtraCharges = Object.entries(supplierCharges)
                .filter(
                    ([chargeType]) =>
                        !isAdvancedTransport ||
                        chargeType.toLowerCase() !== "transport"
                )
                .reduce(
                    (total, [, chargeValue]) =>
                        total + (parseFloat(chargeValue) || 0),
                    0
                );

            const finalSelectedTotal =
                selectedMaterials.length > 0
                    ? selectedMaterialAmountAfterDiscount + totalExtraCharges
                    : selectedMaterialBasePriceSum;

            return sum + finalSelectedTotal;
        }, 0);

        setTotalSelectedQuotationValue(totalSum);
    }, [selectedMaterialsMap, chargeInputs, discountOnBasicAmountInputs, isAdvancedTransport, sortedSuppliers, rfqData?.rfqType]);


    useEffect(() => {
        if (!selectedVendor) return;
        if (rfqData?.rfqType !== 'material') return;

        const quotation =
            selectedVendor.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (!quotation?.materialDetails) {
            setTotalLowestPriceSum(0);
            return;
        }

        const total = quotation.materialDetails.reduce((sum, mat) => {
            const qty = mat.quantity || 0;

            const discount = mat.discount || 0;
            const discountedRate =
                mat.basePrice - (mat.basePrice * discount) / 100;

            const lineTotal =
                discountedRate * qty + (mat.transportCost || 0);

            return sum + lineTotal;
        }, 0);

        setTotalLowestPriceSum(total);
    }, [selectedVendor, rfqData]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full border-2 mb-2 border-t-2 border-orange-500 border-t-gray-200 h-8 w-8"></div>
                <p className="text-sm font-medium">Loading RFQ details...</p>
            </div>
        );
    }

    if (!rfqData) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] flex flex-col items-center justify-center">
                <p className="text-sm text-gray-600">RFQ not found</p>
                <button
                    onClick={() => navigate('/rfqs/workspace/pending-approvals')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Back to Pending Approvals
                </button>
            </div>
        );
    }

    const isNegotiationOnlyView =
        showApprovalSummary || hasLoggedInUserApproved || rfqData?.status === "APPROVED";

    if (activeMobileTab === "negotiation" || isNegotiationOnlyView) {
        return (
            <MobileApprovalNegotiationSummary
                rfqData={rfqData}
                onBack={() => {
                    if (isNegotiationOnlyView) {
                        navigate('/rfqs/workspace/pending-approvals');
                        return;
                    }

                    setActiveMobileTab("selectedRates");
                }}
                tabs={
                    <MobileApprovalTabs
                        activeTab="negotiation"
                        onChange={setActiveMobileTab}
                        selectedRatesDisabled={isNegotiationOnlyView}
                    />
                }
            />
        );
    }

    const handleVendorSelect = (index) => {
        setSelectedVendorIndex(index);
    };


    // console.log('selectedVendor:', selectedVendor?.supplierId);
    // console.log(
    //     'selectedMaterialsMap for vendor:',
    //     selectedMaterialsMap?.[selectedVendor?.supplierId]
    // );

    const quotation =
        selectedVendor?.vendorQuotations?.quotationSets?.slice(-1)[0];

    const getNegotiationOutcome = (supplier) => {
        const sets = supplier?.vendorQuotations?.quotationSets || [];
        if (sets.length < 2) {
            return { amount: 0, percentage: 0 };
        }

        const first =
            (sets[0]?.totalWithoutGst || 0) +
            (sets[0]?.othersTotalWithoutGst || 0);

        const last =
            (sets[sets.length - 1]?.totalWithoutGst || 0) +
            (sets[sets.length - 1]?.othersTotalWithoutGst || 0);

        const amount = first - last;
        const percentage = first > 0 ? ((amount / first) * 100) : 0;

        return {
            amount,
            percentage,
        };
    };

    const getVendorMaterials = () => {
        return (
            selectedVendor?.vendorQuotations?.quotationSets
                ?.slice(-1)[0]
                ?.materialDetails || []
        );
    };

    const areAllMaterialsSelected = () => {
        if (!selectedVendor) return false;

        const vendorMaterials = getVendorMaterials();
        const selected = selectedMaterialsMap?.[selectedVendor.supplierId] || [];

        return (
            vendorMaterials.length > 0 &&
            vendorMaterials.every(vm =>
                selected.some(sm => String(sm.materialId) === String(vm.id || vm.materialId))
            )
        );
    };

    const handleSelectAllMaterials = () => {
        if (!selectedVendor) return;

        const vendorId = selectedVendor.supplierId;
        const vendorMaterials = getVendorMaterials();

        setSelectedMaterialsMap(prev => {
            const updated = { ...prev };

            // If already fully selected → unselect everything
            if (areAllMaterialsSelected()) {
                // remove only this vendor's materials
                delete updated[vendorId];
                return updated;
            }

            const selectedQuantityForMaterial = (materialId) =>
                Object.entries(updated || {}).reduce((sum, [currentVendorId, materials]) => {
                    if (String(currentVendorId) === String(vendorId)) return sum;
                    const selectedMaterial = materials?.find(
                        item => String(item.materialId) === String(materialId)
                    );
                    return sum + getSelectedQuantity(selectedMaterial, materialId);
                }, 0);

            const currentVendorMaterials = (updated[vendorId] || []).filter(
                item => !vendorMaterials.some(
                    vm => String(item.materialId) === String(vm.id || vm.materialId)
                )
            );

            const splitAwareSelections = vendorMaterials.map(mat => {
                const rfqMaterial = rfqData.materials?.find(
                    m => String(m._id) === String(mat.id || mat.materialId)
                );

                if (!rfqMaterial) return null;

                const discount = mat.discount || 0;
                const rate =
                    mat.basePrice - (mat.basePrice * discount) / 100;
                const remainingQuantity = parseNumber(rfqMaterial.quantity) - selectedQuantityForMaterial(rfqMaterial._id);
                if (remainingQuantity <= 0) return null;
                const selectedQuantity = remainingQuantity;
                const selectedTransportCost = calculateSelectedTransportCost(
                    mat.transportRate || 0,
                    selectedQuantity,
                    mat.transportCost || 0
                );

                return {
                    materialId: rfqMaterial._id,
                    quantity: rfqMaterial.quantity,
                    selectedQuantity,
                    selectedDiscount: discount,
                    selectedTransportRate: mat.transportRate || 0,
                    selectedTransportCost,
                    selectedItemRate: rate,
                    selectedSupplierPrice: calculateSelectedSupplierPrice(
                        rate,
                        selectedQuantity,
                        selectedTransportCost
                    ),
                };
            }).filter(Boolean);

            updated[vendorId] = [...currentVendorMaterials, ...splitAwareSelections];
            if (updated[vendorId].length === 0) delete updated[vendorId];

            return updated;
        });
    };

    const getSelectedLabourQuantityForItem = (labourId, excludeSupplierId, sourceMap = selectedMaterialsMap) =>
        Object.entries(sourceMap || {}).reduce((sum, [supplierId, labourItems]) => {
            if (excludeSupplierId && String(supplierId) === String(excludeSupplierId)) return sum;
            const selectedLabour = (labourItems || []).find(item => String(item.labourId) === String(labourId));
            const quantity = selectedLabour?.selectedQuantity ?? 0;
            return sum + (Number(quantity) || 0);
        }, 0);

    const handleLabourSelect = (supplier, labour) => {
        setSelectedMaterialsMap(prev => {
            const updated = Object.entries(prev || {}).reduce((acc, [vendorId, items]) => {
                acc[vendorId] = [...(items || [])];
                return acc;
            }, {});
            const vendorItems = updated[supplier.supplierId] || [];
            const existingSelection = vendorItems.find(item => String(item.labourId) === String(labour.id));

            if (existingSelection) {
                updated[supplier.supplierId] = vendorItems.filter(item => String(item.labourId) !== String(labour.id));
                if (updated[supplier.supplierId].length === 0) delete updated[supplier.supplierId];
                return updated;
            }

            const selectedQuantity = Math.max(
                Number(labour.quantity || 0) - getSelectedLabourQuantityForItem(labour.id, supplier.supplierId, updated),
                0
            );

            // add to selected vendor
            updated[supplier.supplierId] = [
                ...(updated[supplier.supplierId] || []),
                {
                    labourId: labour.id,
                    quantity: labour.quantity,
                    selectedQuantity,
                    selectedLabourRate: labour.labourRate,
                    selectedSupplierPrice:
                        labour.labourRate * selectedQuantity,
                },
            ];

            return updated;
        });
    };

    const normalizeLabourQuantityInput = (value) => {
        const sanitized = String(value || "").replace(/[^\d.]/g, "");
        const [integerPart, decimalPart = ""] = sanitized.split(".");
        return decimalPart !== undefined && sanitized.includes(".")
            ? `${integerPart}.${decimalPart.slice(0, 2)}`
            : integerPart;
    };

    const handleLabourQuantityChange = (supplierId, labour, value) => {
        const normalizedValue = normalizeLabourQuantityInput(value);

        setSelectedMaterialsMap(prev => {
            const updated = Object.entries(prev || {}).reduce((acc, [vendorId, items]) => {
                acc[vendorId] = [...(items || [])];
                return acc;
            }, {});
            const labourEntry = updated[supplierId]?.find(item => String(item.labourId) === String(labour.id));
            if (!labourEntry) return prev;

            const selectedQuantity = Number(normalizedValue) || 0;
            labourEntry.selectedQuantity = normalizedValue;
            labourEntry.quantity = labour.quantity;
            labourEntry.selectedSupplierPrice = (Number(labourEntry.selectedLabourRate ?? labour.labourRate) || 0) * selectedQuantity;

            return updated;
        });
    };


    const SummaryRow = ({ label, value, valueClass = '' }) => (
        <div className="flex justify-between text-xs gap-3">
            <span className="text-gray-600">{label}</span>
            <span className={`font-medium text-right ${valueClass}`}>
                {value}
            </span>
        </div>
    );

    const extraChargeData = selectedVendor
        ? calculateVendorExtraCharges(selectedVendor.supplierId)
        : { charges: [], total: 0 };

    const format = (value = 0) =>
        new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0,
        }).format(Number(value) || 0);

    const getInitials = (name) => {
        if (!name) return '';
        const names = name.split(' ');
        return names.slice(0, 4).map(name => name.charAt(0).toUpperCase()).join('');
    };


    const calculatedGst =
        (sortedSuppliers?.[0]?.vendorQuotations?.grandTotalWithGst - sortedSuppliers?.[0]?.vendorQuotations?.grandTotalWithoutGst || 0)

    const getComparableVendorTotalWithoutGst = (supplier) => {
        if (!supplier) return 0;

        if (
            (rfqData?.rfqType === 'material' || rfqData?.rfqType === 'labour') &&
            hasSelectedEntriesForVendor(supplier)
        ) {
            return (
                getSelectedVendorBasicAmount(supplier) +
                (calculateVendorExtraCharges(supplier.supplierId)?.total || 0)
            );
        }

        return Number(supplier?.vendorQuotations?.grandTotalWithoutGst || 0);
    };

    const getDifferenceFromLowest = () => {
        if (!selectedVendor || !sortedSuppliers?.length) return 0;

        const lowestVendor = sortedSuppliers[0];
        const currentTotal = getComparableVendorTotalWithoutGst(selectedVendor);
        const comparableTotals = sortedSuppliers
            .map(getComparableVendorTotalWithoutGst)
            .filter(total => Number(total) > 0);

        const lowestTotal = comparableTotals.length
            ? Math.min(...comparableTotals)
            : 0;

        return currentTotal - lowestTotal;

        // MATERIAL → selection based
        if (rfqData?.rfqType === 'material') {
            return (
                Number(totalSelectedQuotationValue) -
                Number(lowestVendor.vendorQuotations?.grandTotalWithoutGst || 0)
            );
        }

        // LABOUR & CONTRACTOR → vendor quote based (DESKTOP LOGIC)
        return (
            Number(selectedVendor.vendorQuotations?.grandTotalWithoutGst || 0) -
            Number(lowestVendor.vendorQuotations?.grandTotalWithoutGst || 0)
        );
    };


    const getMaterialTotalBasicAmount = (supplier) => {
        const latestSet =
            supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (!latestSet) return 0;

        // DESKTOP LOGIC: material total without GST
        return Number(latestSet.totalWithoutGst || 0);
    };

    const hasSelectedEntriesForVendor = (supplier) => {
        if (!supplier) return false;

        if (rfqData?.rfqType === 'contractor') {
            return selectedVendors?.some(vendorId => String(vendorId) === String(supplier.supplierId));
        }

        return (selectedMaterialsMap?.[supplier.supplierId]?.length || 0) > 0;
    };

    const getMaterialSelectedBasicAmount = (supplier) => {
        if (!supplier) return 0;
        const selectedMaterialAmount = (selectedMaterialsMap?.[supplier.supplierId] || []).reduce(
            (sum, item) => sum + Number(item.selectedSupplierPrice || 0),
            0
        );

        return Math.max(0, selectedMaterialAmount - getDiscountOnBasicAmount(supplier, selectedMaterialAmount));
    };

    const getMaterialSelectedBasicAmountBeforeDiscount = (supplier) => {
        if (!supplier) return 0;
        return (selectedMaterialsMap?.[supplier.supplierId] || []).reduce(
            (sum, item) => sum + Number(item.selectedSupplierPrice || 0),
            0
        );
    };

    const getMaterialSelectedGstAmount = (supplier) => {
        if (!supplier) return 0;

        const vendorId = supplier.supplierId;
        const selectedItems = selectedMaterialsMap?.[vendorId] || [];
        if (!selectedItems.length) return 0;

        const latestSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const materialDetails = latestSet?.materialDetails || [];
        const otherCharges = latestSet?.otherCharges || {};

        const selectedMaterialAmount = getMaterialSelectedBasicAmountBeforeDiscount(supplier);
        const selectedMaterialAmountAfterDiscount = getMaterialSelectedBasicAmount(supplier);
        const discountRatio = selectedMaterialAmount > 0
            ? selectedMaterialAmountAfterDiscount / selectedMaterialAmount
            : 1;

        const materialGst = selectedItems.reduce((sum, item) => {
            const vendorMaterial = materialDetails.find(
                mat => String(mat.id) === String(item.materialId) || String(mat.materialId) === String(item.materialId)
            );
            const gstPercent = Number(vendorMaterial?.gst || 0);
            const discountedLineAmount = Number(item.selectedSupplierPrice || 0) * discountRatio;
            return sum + ((discountedLineAmount * gstPercent) / 100);
        }, 0);

        const extraChargeGst = Object.keys(rfqData?.otherCharges || {})
            .filter(type => rfqData?.otherCharges?.[type])
            .filter(type => !isAdvancedTransport || type.toLowerCase() !== 'transport')
            .reduce((sum, type) => {
                const amount = Number(chargeInputs?.[vendorId]?.[type] ?? otherCharges?.[type]?.mrp ?? 0);
                const gstPercent = Number(otherCharges?.[type]?.gst || 0);
                return sum + (amount * gstPercent) / 100;
            }, 0);

        return materialGst + extraChargeGst;
    };

    const getMaterialSelectedTotalWithGst = (supplier) => {
        const base = getMaterialSelectedBasicAmount(supplier);
        if (!base) return 0;
        return base + (calculateVendorExtraCharges(supplier.supplierId)?.total || 0) + getMaterialSelectedGstAmount(supplier);
    };

    const getLabourSelectedBasicAmount = (supplier) => {
        if (!supplier) return 0;
        return (selectedMaterialsMap?.[supplier.supplierId] || []).reduce(
            (sum, item) => sum + Number(item.selectedSupplierPrice || 0),
            0
        );
    };

    const getLabourSelectedGstAmount = (supplier) => {
        if (!supplier) return 0;

        const vendorId = supplier.supplierId;
        const selectedItems = selectedMaterialsMap?.[vendorId] || [];
        if (!selectedItems.length) return 0;

        const latestSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const labourItems = latestSet?.labourItems || [];
        const otherCharges = latestSet?.otherCharges || {};

        const labourGst = selectedItems.reduce((sum, item) => {
            const vendorLabour = labourItems.find(labour => labour.id === item.labourId);
            const gstPercent = Number(vendorLabour?.gst || 0);
            return sum + ((Number(item.selectedSupplierPrice || 0) * gstPercent) / 100);
        }, 0);

        const extraChargeGst = Object.keys(rfqData?.otherCharges || {})
            .filter(type => rfqData?.otherCharges?.[type])
            .reduce((sum, type) => {
                const amount = Number(chargeInputs?.[vendorId]?.[type] ?? otherCharges?.[type]?.mrp ?? 0);
                const gstPercent = Number(otherCharges?.[type]?.gst || 0);
                return sum + (amount * gstPercent) / 100;
            }, 0);

        return labourGst + extraChargeGst;
    };

    const getLabourSelectedTotalWithGst = (supplier) => {
        const base = getLabourSelectedBasicAmount(supplier);
        if (!base) return 0;
        return base + (calculateVendorExtraCharges(supplier.supplierId)?.total || 0) + getLabourSelectedGstAmount(supplier);
    };

    const getSelectedVendorBasicAmount = (supplier) => {
        if (!supplier) return 0;

        if (rfqData?.rfqType === 'material') {
            return hasSelectedEntriesForVendor(supplier)
                ? getMaterialSelectedBasicAmount(supplier)
                : getMaterialTotalBasicAmount(supplier);
        }

        if (rfqData?.rfqType === 'labour') {
            return hasSelectedEntriesForVendor(supplier)
                ? getLabourSelectedBasicAmount(supplier)
                : getLabourTotalBasicAmountLive(supplier);
        }

        if (rfqData?.rfqType === 'contractor') {
            return getContractorTotalBasicAmount(supplier);
        }

        return 0;
    };

    const getSelectedVendorGstAmount = (supplier) => {
        if (!supplier) return 0;

        if (rfqData?.rfqType === 'material') {
            return hasSelectedEntriesForVendor(supplier)
                ? getMaterialSelectedGstAmount(supplier)
                : Number(supplier?.vendorQuotations?.grandTotalWithGst || 0) - Number(supplier?.vendorQuotations?.grandTotalWithoutGst || 0);
        }

        if (rfqData?.rfqType === 'labour') {
            return hasSelectedEntriesForVendor(supplier)
                ? getLabourSelectedGstAmount(supplier)
                : Number(supplier?.vendorQuotations?.grandTotalWithGst || 0) - Number(supplier?.vendorQuotations?.grandTotalWithoutGst || 0);
        }

        return Number(supplier?.vendorQuotations?.grandTotalWithGst || 0) - Number(supplier?.vendorQuotations?.grandTotalWithoutGst || 0);
    };

    const getSelectedVendorTotalWithGst = (supplier) => {
        if (!supplier) return 0;

        if (rfqData?.rfqType === 'material') {
            return hasSelectedEntriesForVendor(supplier)
                ? getMaterialSelectedTotalWithGst(supplier)
                : Number(supplier?.vendorQuotations?.grandTotalWithGst || 0);
        }

        if (rfqData?.rfqType === 'labour') {
            return hasSelectedEntriesForVendor(supplier)
                ? getLabourSelectedTotalWithGst(supplier)
                : Number(supplier?.vendorQuotations?.grandTotalWithGst || 0);
        }

        return Number(supplier?.vendorQuotations?.grandTotalWithGst || 0);
    };


    const isLowestRate = (current, lowest) =>
        Number(current.toFixed(2)) === Number(lowest.toFixed(2));

    const formatINR = (value = 0) =>
        new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })
            .format(Number(value) || 0);


    const hasAnyMaterialSelected = (vendorId) =>
        (selectedMaterialsMap?.[vendorId]?.length || 0) > 0;

    const isContractorVendorSelected = (vendorId) =>
        selectedVendors?.some(selectedVendorId => String(selectedVendorId) === String(vendorId));

    const showSelectedValueColumn = rfqData?.rfqType === 'contractor'
        ? (selectedVendors?.length || 0) > 0
        : Object.values(selectedMaterialsMap || {}).some(
            items => (items?.length || 0) > 0
        );

    const handleContractorVendorToggle = (supplier) => {
        if (isEditRatesLocked) return;

        const vendorId = supplier.supplierId;

        // UNSELECT
        if (isContractorVendorSelected(vendorId)) {
            setSelectedVendors([]);
            setSelectedMaterialsMap({});
            setChargeInputs({}); // CLEAR UI CHARGES
            setTotalSelectedQuotationValue(0);
            return;
        }

        // SELECT
        setSelectedVendors([vendorId]);
        setSelectedMaterialsMap(buildContractorScopeMapForVendor(supplier));

        // KEEP ONLY THIS VENDOR'S CHARGES
        setChargeInputs(prev => ({
            [vendorId]: prev?.[vendorId] || {}
        }));

        setTotalSelectedQuotationValue(
            Number(
                supplier.vendorQuotations?.grandTotalWithoutGst || 0
            )
        );
    };

    const getPreviousApprovers = () => {
        const comments = rfqData?.commentsToApprover || [];

        if (!comments.length) return [];

        const map = new Map();

        comments.forEach(c => {
            const name =
                c.addedByName ||
                c.addedBy ||
                'Approver';

            const date = c.date;

            if (!date) return;

            const key = `${name}-${date}`;

            if (!map.has(key)) {
                map.set(key, {
                    name,
                    date,
                });
            }
        });

        return Array.from(map.values());
    };

    const getFirstContractorSectionRate = (sectionId) => {
        if (!selectedVendor) return 0;

        const firstSet =
            selectedVendor.vendorQuotations?.quotationSets?.[0];

        const section =
            firstSet?.contractorDetails?.find(
                s => s.id === sectionId
            );

        return Number(section?.sectionTotalExclGst || 0);
    };


    const getCreatedDate = () => {
        const date =
            rfqData?.createdAt ||
            rfqData?.createdOn ||
            rfqData?.createdDate;

        if (!date) return null;

        return formatDate(date);
    };

    const formatDate = (date) => {
        if (!date) return '';

        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();

        return `${day}/${month}/${year}`;
    };

    const remainingBudget =
        (Number(rfqData?.rfqBudget) || 0) -
        (Number(totalSelectedQuotationValue) || 0);

    const approvalStatus =
        rfqData?.approvalDetails?.find(
            (a) => a?.approvedBy?.userId === user?.userId
        )?.status || rfqData?.approvalDetails?.[0]?.status;

    const currentPendingApproval =
        [...(rfqData?.approvalDetails || [])]
            .sort((a, b) => Number(a?.level || 0) - Number(b?.level || 0))
            .find((approval) => String(approval?.status).toUpperCase() === "PENDING");

    const pendingApproverName =
        currentPendingApproval?.approvers?.find((approver) => approver?.name)?.name || "";

    const approvedApprovers =
        rfqData?.approvalDetails
            ?.filter(a => a.status === "APPROVED" && a?.approvedBy)
            ?.map(a => ({
                name: a.approvedBy.name,
                date: a.approvedAt
            })) || [];

    return (
        <div className="md:hidden min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
            {requestUpdateSubmitting && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-[2px] px-5">
                    <div className="w-full max-w-[340px] rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-2xl">
                        <div className="flex items-center gap-4">
                            <div className="relative h-11 w-11 shrink-0">
                                <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                                <div className="absolute inset-[10px] rounded-full bg-blue-50"></div>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-[#1F1E29]">Requesting quotation update</p>
                                <p className="mt-1 text-xs text-gray-500">Sending email and WhatsApp notification to the vendor...</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showRequestUpdatePopup && (
                <StaticPopupNew header={"Request Quotation Update"} bodyStyle={`pb-0 pt-0`} openModal={showRequestUpdatePopup} handleClose={handleCloseRequestUpdatePopup}>
                    <div className="px-4 py-3 space-y-3">
                        <div>
                            <p className="text-sm font-medium text-[#2D2D11]">{selectedVendor?.businessName || "Vendor"}</p>
                            <p className="text-xs text-gray-500">
                                Note: this message will be sent in the mail and WhatsApp communication message.
                            </p>
                        </div>
                        <label className="block text-xs font-semibold text-[#4F4F45]">
                            Message from {userProfile?.name || "Manager"}
                        </label>
                        <textarea
                            value={requestUpdateMessage}
                            onChange={(event) => setRequestUpdateMessage(event.target.value)}
                            rows={4}
                            className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#1F1E29]"
                            placeholder="Add message for the vendor"
                        />
                        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
                            <button type="button" onClick={handleCloseRequestUpdatePopup} disabled={requestUpdateSubmitting} className="px-4 py-2 text-xs border border-gray-300 rounded-md text-gray-700 disabled:opacity-60">
                                Cancel
                            </button>
                            <button type="button" onClick={handleConfirmRequestQuotationUpdate} disabled={requestUpdateSubmitting} className="px-4 py-2 text-xs rounded-md bg-[#1F1E29] text-white disabled:opacity-60">
                                {requestUpdateSubmitting ? "Requesting..." : "Send Request"}
                            </button>
                        </div>
                    </div>
                </StaticPopupNew>
            )}
            {/* Header */}
            <div className="sticky top-0 z-40 bg-white shadow-sm px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate('/rfqs/workspace/pending-approvals')}
                            className="text-gray-600"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            {/* <h1 className="text-lg font-bold">Selected Rates</h1> */}
                            <h2 className="text-base font-bold">{rfqData.name}</h2>

                        </div>
                    </div>
                </div>
                <MobileApprovalTabs
                    activeTab={activeMobileTab}
                    onChange={setActiveMobileTab}
                />
            </div>

            {/* Content */}
            <div className="p-4">
                {/* RFQ Summary */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                    <div className="space-y-2 text-sm">
                        <div className="flex gap-x-2">
                            <span className="text-gray-600">RFQ No. :</span>
                            <span className="font-medium">{rfqData.rfqId}</span>
                        </div>
                        <div className="flex gap-x-2">
                            <span className="text-gray-600">Project :</span>
                            <span className="font-medium">{rfqData.projectName}</span>
                        </div>

                        <div className="flex gap-x-2">
                            <span className="text-gray-600">RFQ Type :</span>
                            <span className="font-medium capitalize">{rfqData.rfqType}</span>
                        </div>

                        {approvalStatus && (
                            <div className="flex gap-x-2">
                                <span className="text-gray-600">Status :</span>

                                <span
                                    className="font-semibold"
                                >
                                    {rfqData?.status}
                                </span>
                            </div>
                        )}



                        <div className="flex gap-x-2">
                            <span className="text-gray-600">Created By :</span>
                            <span className="font-medium">
                                {rfqData.requesterName}
                                {getCreatedDate() && (
                                    <span>
                                        {' '}| {getCreatedDate()}
                                    </span>
                                )}
                            </span>
                        </div>


                        {/* Total Selected */}
                        {/* <div className="space-y-2 text-sm">
                            <div className="flex gap-x-2">
                                <span className="text-gray-600">
                                    Total Selected Quotation Value:
                                </span>
                                <span className="font-semibold">
                                    Rs.{new Intl.NumberFormat('en-IN').format(totalSelectedQuotationValue)}
                                </span>
                            </div>
                        </div> */}

                        {rfqData.rfqBudget > 0 && (
                            <div className="flex gap-x-2">
                                <span className="text-gray-600">Budget :</span>
                                <span className="font-medium ">
                                    Rs.{new Intl.NumberFormat('en-IN').format(rfqData.rfqBudget)}
                                </span>
                            </div>
                        )}
                        {rfqData.rfqBudget > 0 && (
                            <div className="flex gap-x-2">
                                <span className="text-gray-600">RFQ Value :</span>
                                Rs.{new Intl.NumberFormat('en-IN').format(totalSelectedQuotationValue)}
                                <span
                                    className={`font-semibold gap-x-2 ${remainingBudget < 0 ? 'text-red-600' : 'text-green-600'
                                        }`}
                                >
                                    {remainingBudget < 0 ? ' ▲' : ' ▼'}
                                    Rs.{new Intl.NumberFormat('en-IN', {
                                        maximumFractionDigits: 2
                                    }).format(Math.abs(remainingBudget))}
                                </span>
                            </div>
                        )}

                        <div className="flex gap-x-2">
                            <span className="text-gray-600">Pending with :</span>

                            <span
                                className="font-semibold"
                            >
                                {" "}{pendingApproverName || "-"}
                            </span>
                        </div>

                        <div className="mt-2">
                            <p className="text-gray-600 text-sm">Approved By :</p>

                            {approvedApprovers.length > 0 ? (
                                <div className="mt-1 space-y-1">
                                    {approvedApprovers.map((approver, index) => (
                                        <p key={index} className="text-sm font-medium">
                                            {approver.name} | {formatDate(approver.date)}
                                        </p>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm font-medium text-gray-500">None</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Vendor Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-sm font-bold">Vendor Quotations</h3>

                    <div className="sticky top-[100px] z-20 -mx-4">
                        <div className="flex overflow-x-auto space-x-2 bg-white p-2 shadow-sm scrollbar-hide">
                            {sortedSuppliers.map((supplier, index) => {
                                const isSelected = index === selectedVendorIndex;
                                const rank = supplier.vendorQuotations?.rank || 'N/A';
                                const total = supplier.vendorQuotations?.grandTotalWithoutGst || 0;
                                return (
                                    <button
                                        key={supplier.supplierId}
                                        onClick={() => handleVendorSelect(index)}
                                        className={`flex-shrink-0 border p-2
                                            ${supplier?.isAwarded || hasAnyMaterialSelected(supplier.supplierId)
                                                ? isSelected
                                                    ? 'border-l-[#FF5A1F] border-l-[6px] bg-blue-50'
                                                    : 'border-l-[#FF5A1F] border-l-[6px] bg-white'
                                                : isSelected
                                                    ? 'bg-blue-50'
                                                    : 'border-gray-300'
                                            }
                                            `}

                                    >

                                        <div className="flex flex-col items-center">
                                            <div className="relative flex items-center space-x-2">
                                                {rfqData?.status === "CLOSED" && supplier?.isAwarded && (
                                                    <div className='absolute -top-4 -right-1'>
                                                        <BookmarkIcon style={{ fontSize: 24, color: '#28A745' }} /> {/* Green color */}
                                                    </div>
                                                )}
                                                <div className="w-8 h-8 rounded-full bg-[#343417] flex items-center justify-center">
                                                    <p className="text-xs text-white">
                                                        {getInitials(supplier.businessName)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-left font-medium">{rank}</p>
                                                    {/* <p className="text-xs text-gray-500">
                                                        Rs.{new Intl.NumberFormat('en-IN').format(total)}
                                                    </p> */}
                                                    <p className="text-xs text-gray-500">
                                                        Rs.{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                                            (() => {
                                                                const vendorId = supplier.supplierId;

                                                                if (!showSelectedValueColumn) {
                                                                    return supplier.vendorQuotations?.grandTotalWithoutGst || 0;
                                                                }

                                                                // Contractor → use full quote if selected
                                                                if (rfqData?.rfqType === "contractor") {
                                                                    return selectedVendors?.some(selectedVendorId => String(selectedVendorId) === String(vendorId))
                                                                        ? supplier.vendorQuotations?.grandTotalWithoutGst || 0
                                                                        : 0;
                                                                }

                                                                // Material / Labour → calculate selected total
                                                                const selectedItems =
                                                                    selectedMaterialsMap?.[vendorId] || [];

                                                                const materialTotal = selectedItems.reduce(
                                                                    (sum, item) =>
                                                                        sum + Number(item.selectedSupplierPrice || 0),
                                                                    0
                                                                );

                                                                const extraTotal =
                                                                    calculateVendorExtraCharges(vendorId)?.total || 0;

                                                                return selectedItems.length > 0
                                                                    ? materialTotal + extraTotal
                                                                    : 0;
                                                            })()
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            {/* {isSelected && (
                                            <div className="w-full h-[1px] bg-blue-500 mt-2 rounded-full"></div>
                                         )} */}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <style>
                        {`
                        .overflow-x-auto::-webkit-scrollbar {
                          height: 6px;
                        }

                        .overflow-x-auto::-webkit-scrollbar-thumb {
                          background-color: #949494; 
                        }
                      `}
                    </style>
                    {/* Selected Vendor Details */}
                    {selectedVendor && (
                        <div className="bg-white my-3">
                            {/* Vendor Contact */}
                            <div
                                className="mb-2 p-2 bg-gray-50 border border-gray-200"
                            >
                                <div>
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="text-sm font-bold leading-5">{selectedVendor.businessName} ({selectedVendor.vendorQuotations?.rank || 'N/A'}) </h3>
                                        <div className="flex shrink-0 items-center gap-2">
                                            <QuotationUpdateRequestHistory key={selectedVendor.supplierId} supplier={selectedVendor} />
                                        {canRequestSelectedVendorQuotationUpdate && (
                                            <button
                                                type="button"
                                                onClick={handleRequestQuotationUpdate}
                                                disabled={requestUpdateSubmitting}
                                                className="shrink-0 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700 shadow-sm disabled:opacity-60"
                                            >
                                                Update Quotation
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                    {/* {selectedVendor.contactDetails?.[0]?.mobile && (
                                        <a
                                            href={`tel:${selectedVendor.contactDetails[0].mobile}`}
                                            className="text-sm"
                                        >
                                            {selectedVendor.contactDetails[0].mobile}
                                        </a>
                                    )} */}
                                    <div className="flex items-center gap-2 text-xs text-left font-medium text-[#969688]">
                                        <span className="flex items-center gap-1 text-xs text-[#969688]">
                                            <svg
                                                className="w-3 h-3 shrink-0"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1v3.54a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.54a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 0 01-.24 1.01l-2.25 2.22z" />
                                            </svg>

                                            <span>+91-{selectedVendor?.contactDetails?.[0]?.mobile}</span>
                                        </span>

                                        <span>|</span>

                                        <span>
                                            {selectedVendor?.vendorSource
                                                ? selectedVendor.vendorSource === "MARKETPLACE"
                                                    ? "Marketplace"
                                                    : selectedVendor.vendorSource === "INTERNAL"
                                                        ? "Internal"
                                                        : selectedVendor.vendorSource
                                                : "N/A"}
                                        </span>
                                    </div>
                                </div>
                                {/* <div className="text-right"> */}
                                {/* <p className="text-sm font-bold">
                                    TQV -  Rs.{new Intl.NumberFormat('en-IN').format(
                                        selectedVendor.vendorQuotations?.grandTotalWithoutGst || 0
                                    )}
                                    <span className="text-xs text-gray-600"> (Excl. GST)</span>
                                </p> */}
                            </div>

                            {/* Vendor Materials/Items Table */}
                            {rfqData.rfqType === 'material' && (
                                <div className="mt-4 border rounded-lg bg-white">
                                    {/* <h4 className="text-sm font-bold px-3 py-2 border-b">
                                        Detailed Rate Breakdown
                                    </h4> */}

                                    {/* Vertical scroll like vendor tabs */}
                                    <div className="max-h-[50vh] overflow-y-auto">

                                        <table className="w-full text-xs border-collapse">
                                            <thead className="sticky top-0 bg-gray-100">
                                                <tr>
                                                    <th className="p-2 text-left">
                                                        <label className="flex items-center gap-2 text-xs font-medium">
                                                            <input
                                                                type="checkbox"
                                                                className="w-4 h-4 accent-blue-600"
                                                                disabled={isEditRatesLocked}
                                                                checked={areAllMaterialsSelected()}
                                                                onChange={handleSelectAllMaterials}
                                                            />
                                                            Material
                                                        </label>
                                                    </th>

                                                    <th className="p-2 text-center">Qty</th>
                                                    <th className="p-2 text-center">Budget</th>
                                                    <th className="p-2 text-center">Budget Amt</th>
                                                    <th className="p-2 text-center border-r-2">Lowest Rate</th>
                                                    <th className="p-2 text-center">MRP</th>
                                                    <th className="p-2 text-center">Disc%</th>
                                                    <th className="p-2 text-center">Current Rate</th>
                                                    <th className="p-2 text-center">First Rate</th>
                                                    <th className="p-2 text-center">Diff.</th>
                                                    {/* <th className="p-2 text-center">Transport</th> */}
                                                    <th className="p-2 text-center">Quoted Value</th>
                                                    {showSelectedValueColumn && (
                                                        <th className="p-2 text-center">Selected Value</th>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {rfqData.materials?.map((rfqMaterial) => {
                                                    /* ---------- FIND vendor material (may NOT exist) ---------- */
                                                    const vendorMaterial =
                                                        selectedVendor.vendorQuotations?.quotationSets
                                                            ?.slice(-1)[0]
                                                            ?.materialDetails
                                                            ?.find(
                                                                m =>
                                                                    String(m.id) === String(rfqMaterial._id) ||
                                                                    String(m.materialId) === String(rfqMaterial._id)
                                                            );

                                                    /* ---------- Selected entry ---------- */
                                                    const selectedEntry =
                                                        selectedMaterialsMap?.[selectedVendor.supplierId]?.find(
                                                            m => String(m.materialId) === String(rfqMaterial._id)
                                                        );

                                                    /* ---------- SAFE NUMBERS (DEFAULT = 0) ---------- */
                                                    const basePrice = Number(vendorMaterial?.basePrice) || 0;

                                                    const discount =
                                                    selectedEntry?.selectedDiscount ??
                                                    vendorMaterial?.discount ??
                                                    "";

                                                    const discountedRate =
                                                        basePrice - (basePrice * discount) / 100 || 0;
                                                    const currentRate = selectedEntry
                                                        ? parseNumber(selectedEntry.selectedItemRate, discountedRate)
                                                        : discountedRate;

                                                    const transportCost =
                                                        Number(
                                                            selectedEntry?.selectedTransportCost ??
                                                            vendorMaterial?.transportCost
                                                        ) || 0;

                                                    /* ---------- Lowest rate across ALL vendors ---------- */
                                                    const lowestRate =
                                                        Math.min(
                                                            ...sortedSuppliers
                                                                .map(supplier => {
                                                                    const mat =
                                                                        supplier.vendorQuotations?.quotationSets
                                                                            ?.slice(-1)[0]
                                                                            ?.materialDetails
                                                                            ?.find(
                                                                                m =>
                                                                                    String(m.id) === String(rfqMaterial._id) ||
                                                                                    String(m.materialId) === String(rfqMaterial._id)
                                                                            );

                                                                    if (!mat) return Infinity;

                                                                    const bp = Number(mat.basePrice) || 0;
                                                                    const disc = Number(mat.discount) || 0;

                                                                    return bp - (bp * disc) / 100;
                                                                })
                                                                .filter(v => v !== Infinity)
                                                        ) || 0;

                                                    /* ---------- L1 vendor rate (DESKTOP LOGIC) ---------- */
                                                    const l1Vendor = sortedSuppliers?.[0];

                                                    const l1Material =
                                                        l1Vendor?.vendorQuotations?.quotationSets
                                                            ?.slice(-1)[0]
                                                            ?.materialDetails
                                                            ?.find(
                                                                m =>
                                                                    String(m.id) === String(rfqMaterial._id) ||
                                                                    String(m.materialId) === String(rfqMaterial._id)
                                                            );

                                                    const l1Rate = l1Material
                                                        ? (Number(l1Material.basePrice) -
                                                            (Number(l1Material.basePrice) *
                                                                (Number(l1Material.discount) || 0)) / 100)
                                                        : 0;

                                                    /* ---------- FIRST RATE (DESKTOP LOGIC) ---------- */
                                                    const firstMat =
                                                        selectedVendor.vendorQuotations?.quotationSets?.[0]
                                                            ?.materialDetails
                                                            ?.find(
                                                                m =>
                                                                    String(m.id) === String(rfqMaterial._id) ||
                                                                    String(m.materialId) === String(rfqMaterial._id)
                                                            );

                                                    const firstBase = Number(firstMat?.basePrice) || 0;
                                                    const firstDisc = Number(firstMat?.discount) || 0;

                                                    const firstRate =
                                                        firstBase - (firstBase * firstDisc) / 100;


                                                    const diff = firstRate > 0 ? firstRate - currentRate : 0;


                                                    const lineTotal =
                                                        currentRate * (rfqMaterial.quantity || 0) + transportCost;
                                                    const selectedValue = selectedEntry
                                                        ? Number(selectedEntry.selectedSupplierPrice || 0)
                                                        : null;
                                                    const selectedQuantity = getSelectedQuantity(selectedEntry, rfqMaterial._id);
                                                    const isQuantityExceeded = getSelectedQuantityForMaterial(rfqMaterial._id) > parseNumber(rfqMaterial.quantity);

                                                    return (
                                                        <tr key={rfqMaterial._id} className="border-t align-top">
                                                            {/* Material */}
                                                            <td className="p-2 text-left flex space-x-1 max-w-[500px]">
                                                                <input
                                                                    type="checkbox"
                                                                    className="w-4 h-4 accent-blue-600"
                                                                    disabled={isEditRatesLocked}
                                                                    checked={!!selectedEntry}
                                                                    onChange={() =>
                                                                        toggleMaterialSelection(
                                                                            selectedVendor.supplierId,
                                                                            rfqMaterial,
                                                                            vendorMaterial || {}
                                                                        )
                                                                    }
                                                                />
                                                                {ReactHtmlParser(rfqMaterial.name)}
                                                            </td>

                                                            {/* Qty */}
                                                            <td className="p-2 text-center">
                                                                {rfqMaterial.quantity || 0}{" "}
                                                                {getFormattedUnit(rfqMaterial.unit)}
                                                                {selectedEntry && (
                                                                    <div className="mt-1">
                                                                        <p className="text-[10px] text-gray-500">Selected</p>
                                                                        <input
                                                                            type="text"
                                                                            value={selectedEntry.selectedQuantity ?? selectedQuantity ?? ""}
                                                                            disabled={isEditRatesLocked}
                                                                            onChange={(event) =>
                                                                                handleSelectedQuantityChange(
                                                                                    selectedVendor.supplierId,
                                                                                    rfqMaterial._id,
                                                                                    event.target.value
                                                                                )
                                                                            }
                                                                            className={`w-[56px] rounded border px-1 py-0 text-center text-xs focus:outline-none focus:ring-0 ${isQuantityExceeded ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td className="p-2 text-center">
                                                                Rs.{formatINR(rfqMaterial?.budget)}
                                                            </td>

                                                            <td className="p-2 text-center">
                                                                Rs.{formatINR(rfqMaterial?.budgetAmount)}
                                                            </td>

                                                            <td className="p-2 text-center font-semibold border-r-2">
                                                                Rs.{formatINR(lowestRate)}
                                                            </td>

                                                            {/* MRP */}
                                                            <td className="p-2 text-center font-medium">
                                                                Rs.{formatINR(basePrice)}
                                                            </td>

                                                            {/* Discount */}
                                                            <td className="p-2 text-center">
                                                                <input
                                                                    type="text"
                                                                    disabled={isEditRatesLocked}
                                                                    className="w-full h-[28px] border rounded px-1 text-center text-xs mt-1"
                                                                    value={discount}
                                                                    onChange={(e) =>
                                                                        handleDiscountChange(
                                                                            selectedVendor.supplierId,
                                                                            rfqMaterial._id,
                                                                            e.target.value,
                                                                            vendorMaterial || {},
                                                                            rfqMaterial.quantity
                                                                        )
                                                                    }
                                                                />
                                                                <div className="text-[10px] text-gray-500">
                                                                    {formatINR(vendorMaterial?.discount) || 0}%
                                                                </div>
                                                            </td>

                                                            {/* Current Rate */}
                                                            <td className="p-2 text-center">
                                                                {isEditRatesLocked ? (
                                                                    <span
                                                                        className={`inline-block px-1.5 py-0.5 rounded ${isLowestRate(currentRate, lowestRate)
                                                                            ? 'bg-lime-200 text-lime-800 font-semibold'
                                                                            : 'font-medium'
                                                                            }`}
                                                                    >
                                                                        Rs.{formatINR(currentRate)}
                                                                    </span>
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        title="Edit net item rate"
                                                                        value={selectedEntry ? String(selectedEntry.selectedItemRate ?? "") : String(currentRate || "")}
                                                                        disabled={!selectedEntry}
                                                                        onChange={(event) =>
                                                                            handleNetPriceChange(
                                                                                selectedVendor.supplierId,
                                                                                rfqMaterial._id,
                                                                                event.target.value,
                                                                                vendorMaterial || {}
                                                                            )
                                                                        }
                                                                        onBlur={(event) => {
                                                                            if (event.target.value === "" || event.target.value === ".") {
                                                                                handleNetPriceChange(
                                                                                    selectedVendor.supplierId,
                                                                                    rfqMaterial._id,
                                                                                    "0",
                                                                                    vendorMaterial || {}
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`w-[72px] rounded border px-1 py-0 text-center text-xs focus:outline-none focus:ring-0 ${isLowestRate(currentRate, lowestRate)
                                                                            ? "border-lime-300 bg-lime-100 text-lime-800 font-semibold"
                                                                            : "border-gray-300"
                                                                            } ${!selectedEntry ? "cursor-not-allowed opacity-60" : ""}`}
                                                                    />
                                                                )}
                                                            </td>


                                                            {/* First Rate */}
                                                            <td className="p-2 text-center font-medium">
                                                                {(() => {
                                                                    const firstMat =
                                                                        selectedVendor.vendorQuotations?.quotationSets?.[0]
                                                                            ?.materialDetails
                                                                            ?.find(
                                                                                m =>
                                                                                    String(m.id) === String(rfqMaterial._id) ||
                                                                                    String(m.materialId) === String(rfqMaterial._id)
                                                                            );

                                                                    const firstBase = Number(firstMat?.basePrice) || 0;
                                                                    const firstDisc = Number(firstMat?.discount) || 0;

                                                                    const firstRate =
                                                                        firstBase - (firstBase * firstDisc) / 100;

                                                                    return `Rs.${formatINR(firstRate)}`;
                                                                })()}
                                                            </td>

                                                            {/* Diff */}
                                                            <td
                                                                className={`p-2 text-center font-medium ${diff > 0
                                                                    ? 'text-green-600'
                                                                    : diff < 0
                                                                        ? 'text-red-600'
                                                                        : 'text-gray-600'
                                                                    }`}
                                                            >
                                                                {diff && diff !== 0 ? Math.abs(diff).toFixed(2) : '0'}
                                                                {diff > 0 && ' ▼'}
                                                                {diff < 0 && ' ▲'}
                                                            </td>



                                                            {/* Total */}
                                                            <td className="p-2 text-center font-semibold">
                                                                Rs.{new Intl.NumberFormat('en-IN').format(lineTotal || 0)}
                                                            </td>

                                                            {showSelectedValueColumn && (
                                                                <td className="p-2 text-center font-semibold">
                                                                    {selectedValue != null
                                                                        ? `Rs.${new Intl.NumberFormat('en-IN', {
                                                                            minimumFractionDigits: 0,
                                                                            maximumFractionDigits: 0,
                                                                        }).format(selectedValue)}`
                                                                        : '-'}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>

                                        </table>
                                    </div>
                                </div>
                            )}


                            {rfqData.rfqType === 'labour' && (
                                <div className="mt-4 border rounded-lg bg-white">
                                    <div className="max-h-[50vh] overflow-y-auto">

                                        <table className=" table-fixed text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Skill</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Qty</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Budget</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Budget Amt</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 border-r-2">Lowest Rate</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Current Rate</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Selected Qty</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">First Rate</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Quoted Value</th>
                                                    {showSelectedValueColumn && (
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Selected Value</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {selectedVendor.vendorQuotations?.quotationSets
                                                    ?.slice(-1)[0]
                                                    ?.labourItems
                                                    ?.map((labour, index) => {

                                                        const selectedEntry =
                                                            selectedMaterialsMap?.[selectedVendor.supplierId]
                                                                ?.find(l => l.labourId === labour.id);

                                                        const currentRate =
                                                            selectedEntry?.selectedLabourRate ?? labour.labourRate;

                                                        // FIRST (PREVIOUS) RATE — DESKTOP LOGIC
                                                        const firstLabour =
                                                            selectedVendor.vendorQuotations?.quotationSets?.[0]
                                                                ?.labourItems
                                                                ?.find(l => l.id === labour.id);

                                                        const previousRate = firstLabour?.labourRate ?? labour.labourRate;

                                                        const quotedSelectedValue = Number(
                                                            labour.priceWithoutGst ??
                                                            labour.totalAmount ??
                                                            (Number(labour.labourRate || 0) * Number(labour.quantity || 0))
                                                        );

                                                        const isSelected = !!selectedEntry;
                                                        const total = isSelected
                                                            ? Number(selectedEntry?.selectedSupplierPrice || 0)
                                                            : quotedSelectedValue;
                                                        const selectedValue = isSelected
                                                            ? Number(selectedEntry?.selectedSupplierPrice || 0)
                                                            : quotedSelectedValue;

                                                        // LOWEST CURRENT RATE (DESKTOP LOGIC)
                                                        const allCurrentRates =
                                                            Object.values(selectedMaterialsMap || {})
                                                                .flat()
                                                                .filter(l => l.labourId === labour.id)
                                                                .map(l => Number(l.selectedLabourRate))
                                                                .filter(rate => Number.isFinite(rate));

                                                        const lowestRate =
                                                            allCurrentRates.length > 0
                                                                ? Math.min(...allCurrentRates)
                                                                : currentRate; // fallback when only one vendor


                                                        const rfqLabour = rfqData?.labourDetails?.find(ld =>
                                                            ld.skill === labour.skill
                                                        );
                                                        const selectedQuantityForLabour = getSelectedLabourQuantityForItem(labour.id);
                                                        const isQuantityExceeded = selectedQuantityForLabour > parseNumber(rfqLabour?.quantity ?? labour.quantity);


                                                        return (
                                                            <tr
                                                                key={index}
                                                                className="cursor-default"
                                                            >
                                                                {/* RADIO + SKILL */}
                                                                <td className="px-3 py-2 text-xs">
                                                                    <div className="flex gap-2 items-start min-w-[200px] max-w-[300px]">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={() => handleLabourSelect(selectedVendor, labour)}
                                                                            className="mt-1 accent-orange-600 flex-shrink-0"
                                                                        />

                                                                        <div
                                                                            onClick={(e) => {
                                                                                if (labour?.skill?.length > 200) {
                                                                                    e.stopPropagation();
                                                                                    togglePopup(labour.skill);
                                                                                }
                                                                            }}
                                                                            className="break-words">
                                                                            <div className="line-clamp-2 overflow-hidden">
                                                                                {ReactHtmlParser(labour.skill)}
                                                                            </div>

                                                                            {labour?.skill && labour?.skill !== "N/A" && labour?.skill.length > 200 && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="text-gray-400 text-xs underline italic"
                                                                                >
                                                                                    Read More
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>


                                                                {/* QTY */}
                                                                <td className="px-3 py-2 text-xs">
                                                                    {labour.quantity} {labour.unit}
                                                                </td>

                                                                {/* Budget */}
                                                                <td className="px-3 py-2 text-xs text-center text-gray-700">
                                                                    Rs.{new Intl.NumberFormat('en-IN')
                                                                        .format(rfqLabour?.budget || 0)}
                                                                </td>

                                                                {/* Budget Amount */}
                                                                <td className="px-3 py-2 text-xs text-center text-gray-700">
                                                                    Rs.{new Intl.NumberFormat('en-IN')
                                                                        .format(rfqLabour?.budgetAmount || 0)}
                                                                </td>

                                                                {/* LOWEST RATE */}
                                                                <td className="px-3 py-2 text-xs text-center border-r-2 text-gray-700">
                                                                    Rs.{new Intl.NumberFormat('en-IN').format(lowestRate)}
                                                                </td>


                                                                {/* CURRENT RATE (EDITABLE) + PREVIOUS RATE */}
                                                                <td className="px-3 py-2 text-xs">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        disabled={isEditRatesLocked}
                                                                        value={currentRate}
                                                                        onChange={(e) => {
                                                                            const rate = Number(e.target.value) || 0;

                                                                            setSelectedMaterialsMap(prev => {
                                                                                const updated = Object.entries(prev || {}).reduce((acc, [vendorId, items]) => {
                                                                                    acc[vendorId] = [...(items || [])];
                                                                                    return acc;
                                                                                }, {});
                                                                                const vendorItems = updated[selectedVendor.supplierId] || [];
                                                                                let labourEntry = vendorItems.find(item => String(item.labourId) === String(labour.id));
                                                                                if (!labourEntry) {
                                                                                    const selectedQuantity = Math.max(
                                                                                        Number(labour.quantity || 0) - getSelectedLabourQuantityForItem(labour.id, selectedVendor.supplierId, updated),
                                                                                        0
                                                                                    );
                                                                                    labourEntry = {
                                                                                        labourId: labour.id,
                                                                                        quantity: labour.quantity,
                                                                                        selectedQuantity,
                                                                                        selectedLabourRate: rate,
                                                                                        selectedSupplierPrice: rate * selectedQuantity,
                                                                                    };
                                                                                    updated[selectedVendor.supplierId] = [...vendorItems, labourEntry];
                                                                                }

                                                                                labourEntry.selectedLabourRate = rate;
                                                                                labourEntry.selectedSupplierPrice = rate * Number(labourEntry.selectedQuantity || 0);

                                                                                return updated;
                                                                            });
                                                                        }}
                                                                        className={`w-[60px] h-[28px] border rounded px-1 text-center text-xs
                                                                                 ${isLowestRate(currentRate, lowestRate)
                                                                                ? 'bg-lime-200 text-lime-800 border-lime-500 font-semibold'
                                                                                : 'border-gray-300'
                                                                            }
                                                                         `}
                                                                    />


                                                                    {/* PREVIOUS RATE */}
                                                                    <div className="text-[10px] text-gray-500 text-center mt-0.5">
                                                                        Rs.{new Intl.NumberFormat('en-IN').format(previousRate)}
                                                                    </div>
                                                                </td>

                                                                {/* SELECTED QTY */}
                                                                <td className="px-3 py-2 text-xs text-center">
                                                                    {isSelected ? (
                                                                        <input
                                                                            type="text"
                                                                            value={selectedEntry?.selectedQuantity ?? labour.quantity}
                                                                            disabled={isEditRatesLocked}
                                                                            onChange={(e) => handleLabourQuantityChange(selectedVendor.supplierId, labour, e.target.value)}
                                                                            className={`w-[64px] h-[28px] border rounded px-1 text-center text-xs ${isQuantityExceeded ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                                                        />
                                                                    ) : '-'}
                                                                </td>

                                                                {/* FIRST RATE */}
                                                                <td className="px-3 py-2 text-xs text-center text-gray-600">
                                                                    Rs.{new Intl.NumberFormat('en-IN').format(previousRate || 0)}
                                                                </td>


                                                                {/* TOTAL */}
                                                                <td className="px-3 py-2 text-xs font-medium">
                                                                    Rs.{new Intl.NumberFormat('en-IN').format(total)}
                                                                </td>

                                                                {showSelectedValueColumn && (
                                                                    <td className="px-3 py-2 text-xs font-medium">
                                                                        {isSelected
                                                                            ? `Rs.${new Intl.NumberFormat('en-IN', {
                                                                                maximumFractionDigits: 2,
                                                                            }).format(selectedValue)}`
                                                                            : '-'}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {rfqData.rfqType === 'contractor' && (<>
                                <div className="mt-4 border rounded-lg bg-white">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs border-collapse">

                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="p-2 text-left font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                className="w-4 h-4 accent-orange-600"
                                                                checked={isContractorVendorSelected(
                                                                    selectedVendor.supplierId
                                                                )}
                                                                disabled={isEditRatesLocked}
                                                                onChange={() =>
                                                                    handleContractorVendorToggle(selectedVendor)
                                                                }
                                                            />
                                                            <span>Section Name</span>
                                                        </div>
                                                    </th>

                                                    <th className="p-2 text-right font-medium">
                                                        Budget Amt
                                                    </th>

                                                    <th className="p-2 text-right font-medium border-r-2">
                                                        Lowest
                                                    </th>

                                                    <th className="p-2 text-right font-medium">
                                                        First Quote
                                                    </th>

                                                    <th className="p-2 text-right font-medium">
                                                        Diff.
                                                    </th>

                                                    <th className="p-2 text-right font-medium">
                                                        Current Quote
                                                    </th>

                                                    {showSelectedValueColumn && (
                                                        <th className="p-2 text-right font-medium">
                                                            Selected Value
                                                        </th>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {selectedVendor.vendorQuotations
                                                    ?.quotationSets
                                                    ?.slice(-1)[0]
                                                    ?.contractorDetails
                                                    ?.map((section, index) => {

                                                        const currentRate =
                                                            Number(section.sectionTotalExclGst || 0);

                                                        const lowestRate =
                                                            getLowestContractorSectionRate(section.id);

                                                        const firstRate =
                                                            getFirstContractorSectionRate(section.id);

                                                        const diff =
                                                            firstRate > 0 ? firstRate - currentRate : 0;

                                                        const isLowest =
                                                            lowestRate > 0 &&
                                                            Number(currentRate.toFixed(2)) ===
                                                            Number(lowestRate.toFixed(2));

                                                        const getContractorBudget = (sectionId) => {
                                                            const sec = rfqData?.providingFixingDetails?.find(
                                                                s => s._id === sectionId
                                                            );
                                                            return Number(sec?.budgetAmount || 0);
                                                        };

                                                        return (
                                                            <tr key={section.id || index} className="border-t">
                                                                {/* SECTION NAME */}
                                                                <td className="p-2 flex min-w-[180px] max-w-[260px]">
                                                                    {ReactHtmlParser(section.sectionName)}
                                                                </td>

                                                                {/* Budget amt */}
                                                                <td className="p-2 text-right text-gray-700">
                                                                    Rs.{new Intl.NumberFormat('en-IN')
                                                                        .format(getContractorBudget(section.id))}
                                                                </td>

                                                                {/* LOWEST RATE */}
                                                                <td className="p-2 text-right border-r-2 text-gray-700">
                                                                    Rs.{new Intl.NumberFormat('en-IN').format(lowestRate)}
                                                                </td>


                                                                {/* FIRST RATE */}
                                                                <td className="p-2 text-right text-gray-600">
                                                                    Rs.{new Intl.NumberFormat('en-IN').format(firstRate)}
                                                                </td>

                                                                {/* DIFFERENCE */}
                                                                <td
                                                                    className={`p-2 text-right font-medium ${diff > 0
                                                                        ? 'text-green-600'
                                                                        : diff < 0
                                                                            ? 'text-red-600'
                                                                            : 'text-gray-500'
                                                                        }`}
                                                                >
                                                                    {Math.abs(diff).toFixed(0)}
                                                                    {diff > 0 && ' ▼'}
                                                                    {diff < 0 && ' ▲'}
                                                                </td>

                                                                {/* CURRENT RATE */}
                                                                <td className="p-2 text-right">
                                                                    <span
                                                                        className={`inline-block px-2 py-0.5 rounded ${isLowest
                                                                            ? 'bg-lime-200 text-lime-800 font-semibold'
                                                                            : ''
                                                                            }`}
                                                                    >
                                                                        Rs.{new Intl.NumberFormat('en-IN').format(currentRate)}
                                                                    </span>
                                                                </td>

                                                                {showSelectedValueColumn && (
                                                                    <td className="p-2 text-right font-medium">
                                                                        {isContractorVendorSelected(selectedVendor.supplierId)
                                                                            ? `Rs.${new Intl.NumberFormat('en-IN', {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            }).format(currentRate)}`
                                                                            : '-'}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>)}

                            <div className="flex justify-between text-xs pt-2">
                                <span className="text-gray-600">
                                    {rfqData?.rfqType === 'material'
                                        ? 'Total Material Amount'
                                        : 'Total Basic Amount'}
                                </span>

                                <span className="font-medium">
                                    Rs.
                                    {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                        getSelectedVendorBasicAmount(selectedVendor)
                                    )}
                                </span>
                            </div>

                            {rfqData?.rfqType === "material" && hasSelectedEntriesForVendor(selectedVendor) && (
                                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                                    <div className="flex items-center justify-between gap-2 text-xs">
                                        <div>
                                            <p className="font-medium text-[#6B4E00]">Commercial Discount</p>
                                            <p className="text-[10px] text-[#8A6A00]">Optional, applies before GST</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={
                                                    discountOnBasicAmountInputs?.[selectedVendor?.supplierId] ??
                                                    getLatestQuotationSet(selectedVendor)?.discountOnBasicAmountPercent ??
                                                    ""
                                                }
                                                disabled={isEditRatesLocked}
                                                onChange={(event) =>
                                                    handleDiscountOnBasicAmountChange(
                                                        selectedVendor.supplierId,
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="0"
                                                className={`h-8 w-16 rounded border px-2 text-right text-xs focus:outline-none ${isEditRatesLocked
                                                    ? "border-gray-200 bg-gray-100 text-gray-400"
                                                    : "border-amber-300 bg-white text-[#6B4E00]"
                                                    }`}
                                            />
                                            <span className="text-xs text-[#6B4E00]">%</span>
                                        </div>
                                    </div>
                                    <div className="mt-1 text-right text-[11px] text-[#8A6A00]">
                                        Discount: Rs. {formatINR(getDiscountOnBasicAmount(
                                            selectedVendor,
                                            getMaterialSelectedBasicAmountBeforeDiscount(selectedVendor)
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Extra Charges */}
                            <div className="pt-4 ">
                                <h4 className="text-sm font-medium mb-2">Extra Charges</h4>

                                {/* {extraChargeData.charges.length > 0 ? ( */}
                                <div className="space-y-2">
                                    {extraChargeData.charges.map((charge) => (
                                        <div
                                            key={charge.name}
                                            className="flex justify-between text-xs"
                                        >
                                            <span className="text-gray-600">
                                                {charge.name}
                                            </span>

                                            <span className="font-medium">
                                                Rs.{new Intl.NumberFormat('en-IN').format(charge.amount || 0)}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-between text-xs pt-1 mt-1 border-t">
                                    <span className='font-normal text-gray-600'>Total Extra Charges</span>
                                    <span className="font-medium">
                                        Rs.{new Intl.NumberFormat('en-IN').format(extraChargeData.total)}
                                    </span>
                                </div>
                            </div>

                            {quotation && (
                                <div className="mt-4 bg-white space-y-2">
                                    <h4 className="text-sm font-bold">Quotation Summary</h4>

                                    {/* Total Material / Basic Amount */}
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">
                                            {rfqData?.rfqType === 'material'
                                                ? 'Total Material Amount'
                                                : 'Total Basic Amount'}
                                        </span>

                                        <span className="font-medium">
                                            Rs.
                                            {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                                getSelectedVendorBasicAmount(selectedVendor)
                                            )}
                                        </span>
                                    </div>

                                    {rfqData?.rfqType === "material" && hasSelectedEntriesForVendor(selectedVendor) && (
                                        <SummaryRow
                                            label="Commercial Discount"
                                            value={`Rs. ${formatINR(getDiscountOnBasicAmount(
                                                selectedVendor,
                                                getMaterialSelectedBasicAmountBeforeDiscount(selectedVendor)
                                            ))}`}
                                        />
                                    )}

                                    {/* Extra Charges */}
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">Total Extra Charges</span>
                                        <span className="font-medium">
                                            Rs.{new Intl.NumberFormat('en-IN').format(extraChargeData.total)}
                                        </span>
                                    </div>

                                    {/* Total (Material + Extra Charges) */}
                                    <SummaryRow
                                        label={
                                            rfqData?.rfqType === 'contractor'
                                                ? 'TQV (Basic Amt. + Extra Charges)'
                                                : 'TQV (Material Amt. + Extra Charges)'
                                        }
                                        value={`Rs.${format(
                                            getSelectedVendorBasicAmount(selectedVendor) +
                                            (extraChargeData?.total || 0)
                                        )}`}
                                    />


                                    {/* GST */}
                                    <SummaryRow
                                        label="Total GST"
                                        value={`Rs. ${format(
                                            hasSelectedEntriesForVendor(selectedVendor)
                                                ? getSelectedVendorGstAmount(selectedVendor)
                                                : calculatedGst
                                        )}`}
                                    />

                                    {/* Grand Total */}
                                    <SummaryRow
                                        label="TQV (Incl. GST)"
                                        value={`Rs. ${new Intl.NumberFormat('en-IN').format(
                                            hasSelectedEntriesForVendor(selectedVendor)
                                                ? getSelectedVendorTotalWithGst(selectedVendor)
                                                : selectedVendor?.vendorQuotations?.grandTotalWithGst || 0
                                        )}`}
                                    />

                                    {/* Difference from Lowest */}
                                    <SummaryRow
                                        label="Difference from Lowest Quote"
                                        value={
                                            getDifferenceFromLowest() === 0
                                                ? 'Lowest'
                                                : `Rs. ${format(Math.abs(getDifferenceFromLowest()))}`
                                        }
                                    />


                                    {/* Negotiation Outcome */}
                                    {selectedVendor && (() => {
                                        const { amount, percentage } = getNegotiationOutcome(selectedVendor);

                                        return (
                                            <div className="flex justify-between text-xs">
                                                <div>
                                                    <span className="text-gray-600">Negotiation Outcome</span>
                                                    <p className="text-[10px] text-gray-500">
                                                        (Difference between first & last quote)
                                                    </p>
                                                </div>

                                                <span className="font-medium text-right">
                                                    Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(amount)}
                                                    {percentage !== 0 && (
                                                        <span className="text-gray-500 text-[10px]">
                                                            {' '}({percentage.toFixed(2)}%)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {selectedVendor?.vendorQuotations?.quotationSets?.slice(-1)[0]?.attachments?.length > 0 ? (
                                <div className="mt-4 border-t pt-3">
                                    <p className="text-sm font-bold mb-2">Vendor Attachments</p>

                                    <div className="space-y-2">
                                        {selectedVendor.vendorQuotations.quotationSets
                                            .slice(-1)[0]
                                            .attachments.map((attachment, index) => {
                                                const lastIndex = attachment.Name.lastIndexOf('.');
                                                const ext =
                                                    lastIndex !== -1
                                                        ? attachment.Name.substring(lastIndex + 1)
                                                        : 'file';

                                                return (
                                                    <div
                                                        key={index}
                                                        className="flex items-center mt-2"
                                                    >
                                                        <div
                                                            className="px-1 py-1 flex items-center space-x-1 border border-gray-300 rounded-full text-[#434343] max-w-full"
                                                        >
                                                            {/* FILE ICON (same as desktop) */}
                                                            <img
                                                                src={`/assets/icons/${ext}.png`}
                                                                alt={ext}
                                                                className="h-5 w-5 flex-shrink-0"
                                                                onError={(e) => {
                                                                    e.target.src = '/assets/icons/file.png';
                                                                }}
                                                            />

                                                            {/* FILE NAME */}
                                                            <a
                                                                href={attachment.Content}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                download={attachment.Name}
                                                                className=" text-xs truncate text-[#434343] hover:underline max-w-[220px]"
                                                            >
                                                                {attachment.Name}
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-500 mt-2"></p>
                            )}

                            {/* Vendor Notes */}
                            <div className="mt-4 pt-4 border-t">
                                <h4 className="text-sm font-medium mb-2">Vendor Notes</h4>
                                <p className="text-xs text-gray-700">
                                    {selectedVendor?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes
                                        ? selectedVendor.vendorQuotations.quotationSets.slice(-1)[0].notes
                                        : 'N/A'}
                                </p>
                            </div>


                            {/* Payment Terms */}
                            <div className="mt-4 pt-4 border-t">
                                <h4 className="text-sm font-medium mb-2">Payment Terms</h4>
                                <p className="text-xs text-gray-700">
                                    {selectedVendor.vendorQuotations?.quotationSets?.slice(-1)[0]?.agreedToPaymentTerms
                                        ? 'Agreed to RFQ payment terms'
                                        : selectedVendor.vendorQuotations?.quotationSets?.slice(-1)[0]?.counterPaymentTerms || 'N/A'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <AwardJustificationPanel
                    rfqData={rfqData}
                    userProfile={userProfile}
                    suppliersWithQuotation={sortedSuppliers}
                    selectedMaterialsMap={selectedMaterialsMap}
                    onUpdated={() => {
                        if (entityId && rfqId) {
                            dispatch(fetchRfq({ entityId, rfqId }));
                        }
                    }}
                />
                {/* Approver Comments Section */}
                {Array.isArray(rfqData?.commentsToApprover) &&
                    rfqData.commentsToApprover.length > 0 && (
                        <div className="bg-white rounded-lg shadow-sm border text-xs border-gray-200 p-2 mt-4">
                            <ApproverCommentsSection
                                selectedRatesTab={true}
                                rfqData={rfqData}
                                userProfile={userProfile}
                                approverComment={approverComment}
                                setApproverComment={setApproverComment}
                            />
                        </div>
                    )}
            </div>
            {/* Action Buttons */}
            {hasLoggedInUserApproved ? (
                <div className="sticky bottom-0 bg-blue-50 text-blue-700 w-full border-t text-center p-4 font-medium">
                    This RFQ has already been approved by you
                </div>
            ) : rfqData?.status !== "APPROVED" && (
                <div className="sticky bottom-0 bg-black text-white w-full border-t shadow-lg">
                    {/* <div className="flex space-x-3"> */}
                    <button
                        onClick={() => setShowRejectPopup(true)}
                        className="flex-1 w-1/2 text-center border-solid border-r p-4 font-medium"
                    >
                        Reject
                    </button>
                    <button
                        onClick={() => setShowApprovePopup(true)}
                        className="flex-1 p-4 w-1/2 font-medium"
                    >
                        Approve
                    </button>
                    {/* </div> */}
                </div>)}

            {showRejectPopup && (
                <div className="fixed inset-0 z-50 flex items-end bg-black bg-opacity-40">
                    <div className="w-full bg-white rounded-t-xl p-4">
                        <h3 className="text-sm font-bold mb-2">
                            Reject RFQ
                        </h3>

                        <p className="text-xs text-gray-600 mb-2">
                            Please provide a reason for rejection
                        </p>

                        <textarea
                            rows={4}
                            value={approvalRejectRemark}
                            onChange={(e) => {
                                setApprovalRejectRemark(e.target.value);
                                setRejectError('');
                            }}
                            className="w-full border rounded-md p-2 text-xs focus:outline-none focus:ring-1 focus:ring-red-500"
                            placeholder="Enter rejection remark"
                        />

                        {rejectError && (
                            <p className="text-xs text-red-600 mt-1">
                                {rejectError}
                            </p>
                        )}

                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => {
                                    setShowRejectPopup(false);
                                    setApprovalRejectRemark('');
                                    setRejectError('');
                                }}
                                className="flex-1 border rounded-md py-2 text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleRejectApproval}
                                className="flex-1 bg-red-600 text-white rounded-md py-2 text-sm"
                            >
                                Reject RFQ
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showApprovePopup && (
                <div className="fixed inset-0 z-50 flex items-end bg-black bg-opacity-40">
                    <div className="w-full bg-white rounded-t-xl p-4">
                        <h3 className="text-sm font-bold mb-2">
                            Approve RFQ
                        </h3>

                        <p className="text-xs text-gray-600 mb-2">
                            Add a comment (optional)
                        </p>

                        <textarea
                            rows={4}
                            value={approverComment}
                            onChange={(e) => setApproverComment(e.target.value)}
                            className="w-full border rounded-md p-2 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Enter comment (optional)"
                        />

                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => {
                                    setShowApprovePopup(false);
                                    setApproverComment('');
                                }}
                                className="flex-1 border rounded-md py-2 text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    setShowApprovePopup(false);
                                    handleApproveRfq();
                                }}
                                className="flex-1 bg-green-600 text-white rounded-md py-2 text-sm"
                            >
                                Approve RFQ
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isPopupOpen && selectedDetail && (
                <StaticPopupNew
                    openModal={isPopupOpen}
                    handleClose={() => setIsPopupOpen(false)}
                    width={"lg"}
                    height={80}
                    bodyStyle="pb-0 pt-0 px-0"
                    className="px-0 text-sm"
                    header={"Work Details"}
                >
                    <div className="p-4 max-h-[200px] overflow-y-auto my-auto">
                        <p className="text-xs text-[#3D3D3D]">
                            {ReactHtmlParser(selectedDetail)}
                        </p>
                    </div>
                </StaticPopupNew>
            )}
        </div>
    );
};

export default MobileSelectedRatesView;
